import { OPEN_WORLD_TICK_MS } from '@kenocyber/game-domain/open-world/client';
import {
  BATTLE_TRANSPORT_VERSION,
  type BattleTransportRefusal,
  battleTransportServerFrameSchema,
  isRecoverableBattleRefusal,
  OPEN_WORLD_BASE_PROJECTILE_SCALE_BPS,
  OPEN_WORLD_MIN_AIM_INTERVAL_MS,
  type OpenWorldAllyState,
  type OpenWorldBorderRefusal,
  type OpenWorldBorderState,
  type OpenWorldBossCapacityRefusal,
  type OpenWorldBossEvent,
  type OpenWorldBossHoming,
  type OpenWorldBossRole,
  type OpenWorldBossStrike,
  type OpenWorldBossUltimateState,
  type OpenWorldBoxMark,
  type OpenWorldBoxView,
  type OpenWorldCardBurst,
  type OpenWorldCardSlotFrame,
  type OpenWorldCardVisual,
  type OpenWorldCargoView,
  type OpenWorldChannelEnding,
  type OpenWorldClientFrame,
  type OpenWorldCometEvent,
  type OpenWorldCoordinate,
  type OpenWorldCrossingRefusal,
  type OpenWorldCrossingResult,
  type OpenWorldDirection,
  type OpenWorldEntityState as OpenWorldEntityWireState,
  type OpenWorldErrorCode,
  type OpenWorldExtractionReceipt,
  type OpenWorldFieldItem,
  type OpenWorldFishingPrivate,
  type OpenWorldFishingPublic,
  type OpenWorldFishingRefusalFrame,
  type OpenWorldFishingResult,
  type OpenWorldLootEndReason,
  type OpenWorldLootResult,
  type OpenWorldMerchantPresence,
  type OpenWorldMerchantView,
  type OpenWorldMobEvent,
  type OpenWorldMobStance,
  type OpenWorldPlanetEvent,
  type OpenWorldSaleReceipt,
  type OpenWorldSaleRefusalFrame,
  type OpenWorldServerFrame,
  type OpenWorldStateFrame,
  type OpenWorldSummonEntry,
  openWorldForEachProjectileInBatch,
  openWorldForEachProjectileInVolley,
  openWorldQuadrantLabel,
  openWorldServerFrameSchema,
} from '@kenocyber/shared';
import { OpenWorldAckClock } from './presentation';

/**
 * **O transporte do mundo aberto.**
 *
 * ## Porque é próprio e não o do duelo
 *
 * Porque o que atravessa é outro protocolo, com outra versão, outro bilhete e
 * uma coisa que o duelo não tem: uma **travessia entre salas**. Um duelo vive e
 * morre numa sala; uma expedição muda de sala sem recarregar a página, e é essa
 * a diferença que esta classe existe para tratar.
 *
 * ## O handoff em duas fases, do lado do cliente
 *
 * 1. o jogador pede a travessia; a **origem** continua viva e vulnerável;
 * 2. o servidor responde `destination_ready` com o quadrante de destino;
 * 3. o cliente pede um bilhete e abre a ligação do **destino em segundo plano**,
 *    sem tocar na que está a desenhar;
 * 4. só quando o destino está aberto é que o compromisso segue;
 * 5. quando a origem fecha com `departed`, a fonte de frames **troca**.
 *
 * Nada é recarregado. Nenhuma nave é desenhada duas vezes: os frames da origem
 * deixam de ser lidos no instante em que a troca acontece, e um frame atrasado
 * dela é descartado pela geração que o socket trazia.
 */

export type OpenWorldPhase =
  | 'idle'
  | 'authorising'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'crossing'
  | 'ended'
  | 'failed';

export interface OpenWorldEntity {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly aimX: number;
  readonly aimY: number;
  /**
   * **A intenção de movimento que a autoridade APLICOU** (gate V28I.BX.2).
   *
   * Já viajava no fio para a suavização de um cliente; passa a chegar à cena,
   * que a usa para estimar a velocidade do próprio casco e para levar a última
   * fotografia até ao presente. Nunca decide nada.
   */
  readonly moveX: number;
  readonly moveY: number;
  readonly healthMilli: string;
  readonly maxHealthMilli: string;
  readonly radiusMu: number;
  readonly nickname: string | null;
  readonly appearance: unknown;
  readonly connected: boolean;
  /**
   * **Esta nave voa comigo**, do ponto de vista desta ligação.
   *
   * A sala responde-o por espetador, com a mesma comparação que decide se um
   * tiro aterra. O navegador não escolhe uma equipa e não pinta uma cor por
   * conta própria: ele desenha o que a autoridade disse.
   */
  readonly ally: boolean;
  /**
   * **O que as cartas estão a fazer a este casco, e a desenhar nele.**
   *
   * O que a autoridade disse, sem uma única derivação: as letras são as que a
   * sala escreveu, e a cena desenha-as. Não há aqui um campo para "quanto
   * falta" de um efeito — isso é informação de decisão e só existe para o
   * próprio piloto, na metade privada da cadeira.
   */
  readonly cards: OpenWorldEntityCards;
}

/** As bandeiras e os desenhos de um casco, como o fio os trouxe. */
export interface OpenWorldEntityCards {
  readonly frozen: boolean;
  readonly untargetable: boolean;
  readonly malware: boolean;
  readonly paralysed: boolean;
  readonly slowed: boolean;
  readonly attackBroken: boolean;
  readonly marked: boolean;
  /** Quanta casca de gelo desenhar, de um a três. Zero para nenhuma. */
  readonly frostTier: number;
  /** As letras do que as cartas dele estão a desenhar. */
  readonly visuals: readonly OpenWorldCardVisual[number][];
  /** Quanto da carga já foi cumprido, 0 a 100. `null` quando ninguém carrega. */
  readonly chargePercent: number | null;
  /** Com que força o feixe arde, 0 a 100. `null` quando não há feixe. */
  readonly beamPercent: number | null;
  /** Os dois canhões temporários estão montados. */
  readonly superCannons: boolean;
}

/**
 * As bandeiras e os desenhos, como o fio os escreve.
 *
 * Um subconjunto do estado autoritativo de um casco, e nao um tipo proprio:
 * a entrada e a atualizacao carregam-nos iguais, e a leitura e a mesma.
 */
type OpenWorldCardMarks = Pick<
  OpenWorldEntityWireState,
  'fz' | 'bt' | 'mw' | 'pz' | 'lt' | 'ft' | 'cc' | 'sk' | 'cv' | 'cg' | 'cw' | 'sc'
>;

/**
 * Lê as bandeiras de um estado de casco.
 *
 * Uma função e não um espalhamento em dois sítios: a entrada e a atualização
 * carregam as MESMAS letras, e duas leituras seriam duas oportunidades de uma
 * bandeira nova ficar por ler numa delas.
 */
function entityCardsOf(state: OpenWorldCardMarks): OpenWorldEntityCards {
  return {
    frozen: state.fz === 1,
    untargetable: state.bt === 1,
    malware: state.mw === 1,
    paralysed: state.pz === 1,
    slowed: state.lt === 1,
    attackBroken: state.cc === 1,
    marked: state.sk === 1,
    frostTier: state.ft ?? 0,
    visuals: state.cv ?? [],
    chargePercent: state.cg ?? null,
    beamPercent: state.cw ?? null,
    superCannons: state.sc === 1,
  };
}

/**
 * Um cometa, como o cliente o conhece.
 *
 * O que a autoridade disse e nada mais. Não há aqui um campo para "tem joia":
 * ele não existe no protocolo, e é por isso que o cliente não pode desenhar a
 * diferença nem por acidente.
 */
export interface OpenWorldCometView {
  readonly id: string;
  readonly generation: number;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
  readonly parts: number;
  readonly visualSeed: number;
  /** A máscara das partes presentes. O bit `n` é a parte `n`. */
  readonly mask: number;
  readonly coreExposed: boolean;
  /** A vida do núcleo em milésimos, 0..1000. */
  readonly coreHealth: number;
}

/**
 * Um planeta, como o cliente o conhece.
 *
 * Note o que nao existe aqui: quantos nucleos o planeta tem. So os EXPOSTOS
 * chegam, e nem o campo para os outros existe — que e o que torna impossivel
 * desenhar uma diferenca que nao foi dita.
 */
export interface OpenWorldPlanetView {
  readonly id: string;
  readonly generation: number;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
  readonly parts: number;
  readonly visualSeed: number;
  readonly mask: number;
  /** Os nucleos expostos: qual, e a vida em milesimos. */
  readonly cores: readonly { readonly index: number; readonly health: number }[];
}

/**
 * Um mob, como o cliente o conhece.
 *
 * Note o que nao existe aqui: quem ele esta a perseguir. A postura diz que ele
 * esta a perseguir alguem, e a direcao diz para onde — e as duas juntas chegam
 * para o desenhar sem dizer a ninguem que ha um casco do outro lado do
 * quadrante.
 */
export interface OpenWorldMobView {
  readonly id: string;
  readonly generation: number;
  /** O indice do mapa de onde as estatisticas sairam. Decide o sprite. */
  readonly mapIndex: number;
  readonly x: number;
  readonly y: number;
  readonly dirX: number;
  readonly dirY: number;
  readonly radiusMu: number;
  /** Vida em milli, decimais. `bigint` porque a progressao nao tem teto. */
  readonly health: bigint;
  readonly maxHealth: bigint;
  readonly stance: OpenWorldMobStance;
  readonly poisoned: boolean;
  readonly bleeding: boolean;
  /**
   * **As penalidades que uma carta lhe está a fazer**, tal como a sala as diz.
   *
   * As mesmas letras de um casco, porque é o mesmo estado: um corpo lento é um
   * corpo lento. As de BENEFÍCIO não aparecem — uma criatura não lança cartas —
   * e não há campo onde pudessem aparecer.
   */
  readonly cards: OpenWorldCreatureCards;
}

/**
 * As penalidades de uma CRIATURA, como o fio as trouxe.
 *
 * Um subconjunto do que um casco leva: só as que descrevem movimento, ataque e
 * dano causado. Uma criatura não fica invisível, não fica inalvejável e não
 * ganha pressa, porque nada lhe pode dar nenhuma dessas coisas.
 */
export interface OpenWorldCreatureCards {
  readonly frozen: boolean;
  readonly slowed: boolean;
  readonly paralysed: boolean;
  readonly attackBroken: boolean;
  readonly marked: boolean;
  /** Quanta casca de gelo desenhar, de um a três. Zero para nenhuma. */
  readonly frostTier: number;
}

/** Lê as penalidades de uma criatura. Uma leitura, para as duas espécies. */
function creatureCardsOf(state: {
  readonly fz?: 1 | undefined;
  readonly lt?: 1 | undefined;
  readonly pz?: 1 | undefined;
  readonly cc?: 1 | undefined;
  readonly sk?: 1 | undefined;
  readonly ft?: 1 | 2 | 3 | undefined;
}): OpenWorldCreatureCards {
  return {
    frozen: state.fz === 1,
    slowed: state.lt === 1,
    paralysed: state.pz === 1,
    attackBroken: state.cc === 1,
    marked: state.sk === 1,
    frostTier: state.ft ?? 0,
  };
}

/**
 * Um boss, como o cliente o conhece.
 *
 * Note o que nao existe aqui: o pacote dele. Nem o conteudo, nem a quantidade,
 * nem um campo onde isso pudesse caber — o segredo e mantido pela ausencia da
 * forma. So a recusa por capacidade, dita ao candidato que nao coube, traz um
 * numero, e ela vive noutro sitio.
 */
export interface OpenWorldBossView {
  readonly id: string;
  readonly generation: number;
  readonly mapIndex: number;
  /** Corpo a corpo, ou conjurador. Um sinal discreto, nunca outro sprite. */
  readonly role: OpenWorldBossRole;
  /** A escala desta geracao, em basis points. A variacao canonica de tamanho. */
  readonly scaleBps: number;
  readonly x: number;
  readonly y: number;
  readonly dirX: number;
  readonly dirY: number;
  readonly radiusMu: number;
  readonly health: bigint;
  readonly maxHealth: bigint;
  readonly poisoned: boolean;
  readonly bleeding: boolean;
  /**
   * **As penalidades que uma carta lhe está a fazer**, tal como a sala as diz.
   *
   * As mesmas letras de um casco, porque é o mesmo estado. As de BENEFÍCIO não
   * aparecem — uma criatura não lança cartas — e não há campo onde pudessem.
   */
  readonly cards: OpenWorldCreatureCards;
  /**
   * **A habilidade em curso, ou nada.** Cada um dos vinte tem a sua.
   *
   * A geometria que vem aqui é a MECÂNICA, e é a única que o desenho usa: o
   * cliente não decide quem foi atingido e não tem uma segunda descrição da
   * forma que pudesse discordar desta.
   */
  readonly strike: OpenWorldBossStrike | null;
  /**
   * **A ultimate em curso, ou nada.** Só um dos três finais a tem.
   *
   * `iv` é a resposta sobre MIM, e nunca a lista: cada ligação recebe a dela.
   * O limiar da interrupção não vem — só o progresso, em milésimos.
   */
  readonly ultimate: OpenWorldBossUltimateState | null;
}

/**
 * **Um teleguiado de um final no ar, como o cliente o conhece.**
 *
 * Ele não é um projétil: não colide com mais nada, não é destrutível e não
 * expira. O cliente desenha o que a sala diz que existe, e o dano dele nunca
 * viaja — não há nada a decidir sobre uma coisa que não se pode travar.
 */
export interface OpenWorldBossHomingView {
  readonly id: number;
  /** De quem ele anda atrás, pela entidade pública. */
  readonly targetEntityId: number;
  readonly ability: OpenWorldBossHoming['k'];
  readonly x: number;
  readonly y: number;
  readonly dirX: number;
  readonly dirY: number;
  readonly radiusMu: number;
}

/**
 * **Um núcleo rúnico da Sentença, como o cliente o conhece.**
 *
 * Uma coisa com vida que se pode partir, e que **não paga nada**: sem Ouro, sem
 * XP, sem item e sem crédito de último golpe. A barra dele desenha-se com a
 * vida que vem, e o limiar da interrupção não existe aqui.
 */
export interface OpenWorldBossCoreView {
  readonly id: number;
  readonly bossId: string;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
  readonly health: bigint;
  readonly maxHealth: bigint;
}

/**
 * Uma brasa no chão, como o cliente a conhece.
 *
 * Um LUGAR: entra, arde e apaga-se, e nunca se mexe. Os dois tiques são os da
 * sala, e é contra o relógio autoritativo da trama que o desenho os lê.
 */
export interface OpenWorldEmberView {
  readonly id: number;
  readonly bossId: string;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
  /** `disc` (uma brasa ou o pó) ou `sector` (a névoa pútrida). */
  readonly shape: 'disc' | 'sector';
  readonly dirX: number;
  readonly dirY: number;
  readonly sectorTanBps: number;
  /** A lentidão que ela impõe, em basis points. Zero é "não abranda". */
  readonly slowBps: number;
  readonly bornAtTick: number;
  readonly expiresAtTick: number;
}

/** Uma área aquática na vista. Um lugar, e nada sobre o que vive nela. */
export interface OpenWorldWaterView {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
  readonly visualSeed: number;
}

/**
 * **O que a SALA disse deste corpo.** Imutável desde o nascimento
 * (gate V28I.AK).
 *
 * Estado autoritativo, e nada mais: identidade, rumo, âncora, tipo e
 * linearidade. Nunca é escrito depois de criado — uma atualização da sala
 * substitui a base inteira, e é isso que faz "re-ancorar" ser uma coisa que
 * acontece e não uma que se lembra de acontecer.
 *
 * A posição VISUAL não está aqui de propósito. Ela é derivada, muda a cada
 * trama, e misturá-la com o que a sala disse era o que obrigava a reconstruir
 * o objeto inteiro só para mover dois números.
 */
export interface OpenWorldShotBase {
  readonly id: number;
  /*
   * **O rumo e a âncora MUDAM em pé** (gate V28I.AP).
   *
   * Eles eram `readonly`, e por isso uma atualização de um míssil construía um
   * `base` inteiro novo e um `OpenWorldShot` novo, vinte vezes por segundo e
   * por corpo, para trocar cinco números. A identidade, o que o corpo É e a
   * marca de linearidade continuam imutáveis: o que uma trama pode corrigir é
   * onde ele está e para onde vai.
   */
  vx: number;
  vy: number;
  /**
   * **Que corpo de carta ele é**, ou `null` para um tiro comum.
   *
   * Dito na ENTRADA e nunca deduzido: as cinco coisas desenham-se de maneiras
   * diferentes, e adivinha-lo pelo tamanho seria a cena a decidir arte a partir
   * de um numero de equilibrio.
   */
  readonly card: 'f' | 'c' | 'z' | 't' | 's' | 'w' | 'g' | 'd' | null;
  /** O raio da explosão que ele vai fazer. Só com `card`, e só quando rebenta. */
  readonly blastMu: number | null;
  /**
   * **Quem o disparou**, pela identidade de entidade que a sala publicou.
   *
   * É por aqui que a cena encontra a aparência do atirador — e com ela a cor do
   * canhão —, exactamente como o duelo encontra a cor pela cadeira. Dito na
   * entrada e nunca mudado.
   */
  readonly owner: number;
  /** Que família de corpo ele é: um tiro, uma bomba ou um míssil. Da sala. */
  readonly kind: 'shot' | 'bomb' | 'missile';
  /**
   * **A escala física do corpo, em basis points** (gate V28I.CF.3).
   *
   * Congelada no disparo pela sala e dita na entrada. `10 000` quando a trama
   * não a traz, que é o tamanho-base. A cena desenha o tiro por este número e
   * nunca pela escala interpolada do casco que o disparou.
   */
  readonly scaleBps: number;
  /**
   * **Onde um obus cai**, quando é um obus (gate V28I.CF.1A): o centro do círculo
   * dele, dito na entrada. Ausente em todos os outros corpos.
   */
  readonly targetX?: number;
  readonly targetY?: number;
  /**
   * **Este corpo voa a direito e não vai receber atualizações** (V28I.Z.3).
   *
   * Quando a sala o marca assim, a posição deixa de vir em cada trama e passa a
   * ser derivada: `base + v x (tique - tique_da_base)`. Ausente é o
   * comportamento de sempre — a posição chega, e é essa que vale.
   */
  readonly linear: boolean;
  /** A posição AUTORITATIVA da última coisa que a sala disse deste corpo. */
  baseX: number;
  baseY: number;
  /** E o tique em que ela foi dita. A âncora, e nunca um relógio local. */
  baseTick: number;
}

/**
 * **O registo de APRESENTAÇÃO de um corpo** (gate V28I.AK).
 *
 * Uma alocação por entidade VIVA, e não uma por entidade por quadro: ele nasce
 * quando a sala anuncia o corpo, é movido em pé enquanto ele voa, e morre
 * quando a sala o retira.
 *
 * ## O que ele é, e o que não é
 *
 * `base` é o que a sala disse, e ninguém lhe toca. `x` e `y` são a posição
 * **desenhada**, derivada da base pela aritmética canónica do V28I.Z.3 — a
 * mesma, bit a bit, que viajaria numa atualização.
 *
 * Ele não atravessa protocolo, não entra em instantânea nenhuma, não decide
 * colisão, dano nem expiração, e não altera o estado recebido. Pertence a uma
 * identidade autoritativa e é apagado quando ela sai.
 */
export interface OpenWorldShot {
  readonly base: OpenWorldShotBase;
  /** A posição desenhada. Muda em pé, e é a única coisa aqui que muda. */
  x: number;
  y: number;
}

/**
 * **A extrapolação, escrita uma vez** (gate V28I.Z.3, movida aqui pelo V28I.AK).
 *
 * `base + v x (tique - tique_da_base)`, que é literalmente a aritmética da
 * sala: um tique move um corpo exatamente `(vx, vy)` em milli-unidades
 * inteiras. Não há suavização, não há previsão e não há relógio local — o
 * relógio é o `k` da trama, e uma ligação parada congela os corpos.
 *
 * Escreve na apresentação e **lê** a base. Um corpo não linear e um tique não
 * avançado não mexem em nada, que é exatamente o que acontecia antes.
 */
function advanceOpenWorldShot(shot: OpenWorldShot, tick: number): void {
  const base = shot.base;
  if (!base.linear) {
    return;
  }
  const ticks = tick - base.baseTick;
  if (ticks <= 0) {
    return;
  }
  shot.x = base.baseX + base.vx * ticks;
  shot.y = base.baseY + base.vy * ticks;
}

/** Tudo o que o cliente sabe. Uma fotografia, substituída por inteiro. */
export interface OpenWorldClientState {
  readonly phase: OpenWorldPhase;
  readonly quadrant: OpenWorldCoordinate | null;
  readonly label: string;
  readonly sideMu: number;
  readonly tick: number;
  /** Quando a última trama chegou ao socket, no relógio de apresentação (gate V28I.BX.2). */
  readonly receivedAt: number;
  /**
   * O instante local a que o tique dela corresponde para o próprio casco, ou
   * nada antes do primeiro reconhecimento. Ver `OpenWorldAckClock`.
   */
  readonly ownSyncAt: number | null;
  readonly me: number;
  readonly entities: readonly OpenWorldEntity[];
  readonly shots: readonly OpenWorldShot[];
  readonly comets: readonly OpenWorldCometView[];
  /**
   * Os eventos de cometa desta trama, e só desta.
   *
   * Substituídos por inteiro a cada trama: são efeitos a desenhar uma vez, não
   * estado. A cena drena-os e esquece-os — e um cliente que perca uma trama
   * perde um estilhaço, nunca um facto, porque o estado público da rocha diz a
   * verdade sozinho.
   */
  readonly cometEvents: readonly OpenWorldCometEvent[];
  readonly planets: readonly OpenWorldPlanetView[];
  readonly planetEvents: readonly OpenWorldPlanetEvent[];
  readonly mobs: readonly OpenWorldMobView[];
  /** Os eventos de mob desta trama, e so desta. Drenados e esquecidos. */
  readonly mobEvents: readonly OpenWorldMobEvent[];
  readonly waters: readonly OpenWorldWaterView[];
  /** Quem está a pescar, na vista. Público: entidade, água e fase. */
  readonly fishers: readonly OpenWorldFishingPublic[];
  /**
   * **A minha pescaria**, ou nada.
   *
   * `null` quando não há nenhuma. O peixe dentro dela é `null` até à mordida — e
   * não é um campo escondido, é um campo que ainda não tem valor.
   */
  readonly fishing: OpenWorldFishingPrivate | null;
  /**
   * **A água em que o meu casco está**, dita pelo servidor.
   *
   * `null` quando não está em nenhuma. É ela que decide se o botão existe — o
   * cliente não conclui pela posição que desenha.
   */
  readonly fishingWater: string | null;
  /**
   * **O comerciante deste quadrante**, ou nada. Público: um marco fixo.
   */
  readonly merchant: OpenWorldMerchantPresence | null;
  /**
   * **Estou perto o suficiente para negociar**, dito pelo servidor.
   *
   * É isto que decide se o botão existe. O cliente não conclui pela posição que
   * desenha, e nunca envia uma distância.
   */
  readonly merchantNear: boolean;
  /** O balcão, quando ele foi pedido. Só meu. */
  readonly merchantView: OpenWorldMerchantView | null;
  /** O recibo da última venda, para o painel o mostrar uma vez. */
  readonly saleReceipt: OpenWorldSaleReceipt | null;
  /** A última ordem de venda recusada, dita só a mim. */
  readonly saleRefusal: OpenWorldSaleRefusalFrame | null;
  /** As caixas de morte na vista. Marcas públicas: id, sítio e tempo. */
  readonly boxes: readonly OpenWorldBoxMark[];
  /**
   * **As invocações na vista** (gate V28I.P): o Drone e o Leviatã.
   *
   * Entram inteiras e saem por id, como tudo o resto. Elas movem-se em todos os
   * tiques em que existem, portanto uma entrada é reenviada sempre — não há um
   * canal de "mudou" para elas, e não haveria o que poupar.
   */
  readonly summons: readonly OpenWorldSummonEntry[];
  /**
   * **Os aliados desta expedição, e o pouco que se sabe deles.**
   *
   * Quem está vivo, quem está aos comandos, em que quadrante, e — quando
   * estão noutro — grosso modo para que lado. **Nunca** a posição exata de um
   * aliado distante: uma PT não é uma forma de ver através de paredes, e a
   * área de interesse continua a decidir que entidades existem.
   *
   * A sala só o envia a ligações da mesma composição congelada, portanto esta
   * lista é privada por construção.
   */
  readonly allies: readonly OpenWorldAllyState[];
  /**
   * **As explosões de carta desta trama, e só desta.**
   *
   * Substituídas por inteiro a cada trama: são efeitos a desenhar uma vez, e
   * não estado. A cena drena-as e esquece-as, e uma ressincronização não as
   * traz — repetir uma explosão que já aconteceu seria inventar uma segunda.
   */
  readonly cardBursts: readonly OpenWorldCardBurst[];
  /**
   * **As explosões de ÁREA confirmadas**, do mesmo canal dos efeitos de joia.
   *
   * O kamikaze de um Drone publica-se aqui com a identidade da Bomba Atómica,
   * exatamente como no duelo: a arte que esse id nomeia é a aprovada, e dar-lhe
   * uma segunda seria redesenhar uma coisa que já foi decidida.
   */
  readonly blasts: readonly {
    /**
     * A identidade do evento: a semente da variação, e nunca um sorteio local.
     *
     * Composta do tique e da entidade que a causou, que são os dois números
     * autoritativos que a nomeiam. Duas máquinas compõem a mesma.
     */
    readonly id: number;
    readonly x: number;
    readonly y: number;
    readonly r: number;
  }[];
  /**
   * **Os três espaços de cartas DESTE piloto.**
   *
   * A metade privada da cadeira: o que está a correr, quanto falta, e de quanto
   * é o arrefecimento total para o anel ter denominador. Nunca de outra pessoa.
   *
   * Vazio até a sala falar, que é o estado de uma ligação que acabou de se
   * sentar — a barra desenha três caixas vazias, que é a verdade nesse instante.
   */
  readonly cardSlots: readonly OpenWorldCardSlotFrame[];
  /** A caixa que EU posso abrir, dita pelo servidor. Decide se o botão existe. */
  readonly nearestBox: string | null;
  /** O interior da caixa aberta, e só a mim. `null` quando não há nenhuma. */
  readonly boxView: OpenWorldBoxView | null;
  /** O resultado do último saque. */
  readonly lootResult: OpenWorldLootResult | null;
  /** A lista da minha carga, quando pedida. Privada por inteiro. */
  readonly cargoItems: readonly OpenWorldFieldItem[] | null;
  /** Como a última tentativa acabou, para o painel o dizer uma vez. */
  readonly fishingResult: OpenWorldFishingResult | null;
  /** A última ordem recusada, dita só a mim. */
  readonly fishingRefusal: OpenWorldFishingRefusalFrame | null;
  readonly bosses: readonly OpenWorldBossView[];
  readonly bossEvents: readonly OpenWorldBossEvent[];
  /** Os teleguiados no ar. Substituídos por inteiro: eles mudam sempre. */
  readonly bossHoming: readonly OpenWorldBossHomingView[];
  /** Os núcleos rúnicos no campo. Substituídos por inteiro, pela mesma razão. */
  readonly bossCores: readonly OpenWorldBossCoreView[];
  /** As brasas na vista. Perigo desenhado, e não decoração. */
  readonly embers: readonly OpenWorldEmberView[];
  /**
   * A recusa por falta de espaco, quando ela e para MIM.
   *
   * Substituida por inteiro a cada trama, como os eventos: e uma coisa a dizer
   * uma vez. A cena transforma-a num aviso que se limpa sozinho.
   */
  readonly bossCapacity: OpenWorldBossCapacityRefusal | null;
  readonly border: OpenWorldBorderState | null;
  readonly cargo: OpenWorldCargoView | null;
  readonly receipt: OpenWorldExtractionReceipt | null;
  readonly crossing: OpenWorldCrossingResult | null;
  /** Nulo enquanto a ligação está saudável. Uma frase curta quando não está. */
  readonly notice: string | null;
}

export interface OpenWorldSocket {
  send(data: string): void;
  close(): void;
  onOpen(handler: () => void): void;
  onMessage(handler: (data: string) => void): void;
  onClose(handler: () => void): void;
  onError(handler: () => void): void;
}

/**
 * **Uma capacidade, e para onde ela serve.**
 *
 * `socketUrl` nulo é o caminho de sempre: o Durable Object da mesma origem,
 * com o bilhete na query. Uma cadeia é um host de batalha, e então o bilhete
 * **não vai na URL** — vai na primeira mensagem (ADR 0140).
 *
 * A escolha é do SERVIDOR. Não há aqui nada que o cliente decida: ele recebe um
 * destino e usa-o, e um cliente que pudesse propor o seu escolheria a sua
 * própria autoridade.
 */
export interface OpenWorldTicketGrant {
  readonly ticket: string;
  readonly socketUrl: string | null;
}

export interface OpenWorldConnectionDeps {
  /** Cunha uma capacidade de USO ÚNICO para um quadrante. Chamada em cada ligação. */
  readonly mintTicket: (quadrant: OpenWorldCoordinate) => Promise<OpenWorldTicketGrant | null>;
  /** A URL do caminho antigo: mesma origem, bilhete na query. */
  readonly socketUrl: (ticket: string) => string;
  readonly openSocket: (url: string) => OpenWorldSocket;
  readonly now: () => number;
  /**
   * **O relógio de apresentação da página** (gate V28I.BX.2), só para carimbar
   * a chegada das tramas e a saída dos comandos no MESMO relógio da cena. Sem
   * ele vale `now`. Nenhuma decisão da ligação depende dele.
   */
  readonly presentationNow?: () => number;
  readonly schedule: (run: () => void, ms: number) => () => void;
}

/** Quanto tempo esperar antes de tentar o socket outra vez, depois de uma queda. */
const RECONNECT_DELAY_MS = 500;

/** Quanto tempo uma notícia fica no ecrã. Chega para ler uma frase. */
const NOTICE_MS = 4000;

/**
 * De quanto em quanto tempo uma intenção INALTERADA é repetida.
 *
 * Meio segundo: dez tiques da sala. Chega para uma mensagem perdida não deixar
 * a nave presa a uma direção antiga, e são duas mensagens por segundo em vez de
 * sessenta.
 */
const INTENT_HEARTBEAT_MS = 500;

const EMPTY: OpenWorldClientState = {
  phase: 'idle',
  quadrant: null,
  label: '',
  sideMu: 0,
  tick: 0,
  receivedAt: 0,
  ownSyncAt: null,
  me: 0,
  entities: [],
  shots: [],
  comets: [],
  cometEvents: [],
  planets: [],
  planetEvents: [],
  mobs: [],
  mobEvents: [],
  bosses: [],
  bossEvents: [],
  bossHoming: [],
  bossCores: [],
  embers: [],
  bossCapacity: null,
  waters: [],
  fishers: [],
  fishing: null,
  fishingWater: null,
  merchant: null,
  merchantNear: false,
  merchantView: null,
  saleReceipt: null,
  saleRefusal: null,
  boxes: [],
  summons: [],
  allies: [],
  cardBursts: [],
  blasts: [],
  cardSlots: [],
  nearestBox: null,
  boxView: null,
  lootResult: null,
  cargoItems: null,
  fishingResult: null,
  fishingRefusal: null,
  border: null,
  cargo: null,
  receipt: null,
  crossing: null,
  notice: null,
};

/**
 * **Uma recusa da SALA, em português** (gate V28I.T).
 *
 * Exaustiva sobre `OPEN_WORLD_ERROR_CODES`: um quinto código não compila sem
 * a frase que o descreve. O `default` existe na mesma, e é deliberado — uma
 * sala a correr uma versão mais recente pode mandar um código que este build
 * não conhece, e a resposta certa é uma frase segura e não um ecrã vazio.
 *
 * **Nenhuma delas revela nada.** Sem pilha, sem identificador interno, sem
 * semente, sem nome de objeto durável e sem o valor que foi recusado: o que
 * viaja é um código de quatro, e o que se mostra é o que ele significa para
 * quem está a jogar.
 */
function errorText(code: OpenWorldErrorCode): string {
  switch (code) {
    case 'protocol':
      return 'O pedido não foi entendido. Tente outra vez.';
    case 'rate_limit':
      return 'Muitos pedidos seguidos. Aguarde um instante.';
    case 'not_seated':
      return 'A sua nave ainda não está neste quadrante.';
    case 'unavailable':
      return 'O quadrante não conseguiu responder agora. Nada mudou.';
    default:
      return 'O quadrante recusou o pedido. Nada mudou.';
  }
}

/**
 * Uma recusa de fronteira, em português. O código fica para o log.
 *
 * Exaustiva de propósito: um `default` genérico esconderia uma razão nova atrás
 * de "não foi possível", e a razão é a única coisa útil numa recusa.
 */
/** Porque é que a interação com uma caixa acabou. Legível, e nunca um código. */
function lootEndText(reason: OpenWorldLootEndReason): string {
  switch (reason) {
    case 'closed':
      return 'Caixa fechada.';
    case 'out_of_range':
      return 'Afastou-se demasiado da caixa.';
    case 'moved':
      return 'Mover-se fechou a caixa.';
    case 'fired':
      return 'Disparar fechou a caixa.';
    case 'destroyed':
      return 'A nave caiu.';
    case 'crossing':
      return 'A travessia fechou a caixa.';
    case 'damaged':
      return 'Levar dano fechou a caixa.';
    case 'disconnected':
      return 'A ligação caiu.';
    case 'no_box':
      return 'A caixa já não está aqui.';
    default:
      return 'A caixa fechou.';
  }
}

/** Porque uma extração não aconteceu, dito ao piloto (gate V28I.BY). */
function extractionRefusalText(refusal: string | null): string {
  switch (refusal) {
    case 'already_sealed':
      return 'Esta expedição já terminou: a carga já tinha sido entregue.';
    case 'no_cargo':
      return 'Não há carga de campo para extrair.';
    case 'vault_refused':
      return 'O inventário recusou um item. Nada foi extraído, e a carga continua consigo.';
    case 'gold_overflow':
      return 'O Ouro não cabe na carteira. Nada foi extraído, e a carga continua consigo.';
    default:
      return 'A extração não pôde ser concluída. A carga continua consigo; tente outra vez.';
  }
}

function borderRefusalText(refusal: OpenWorldBorderRefusal): string {
  switch (refusal) {
    case 'not_in_strip':
      return 'A nave não está inteira na faixa da fronteira.';
    case 'already_channelling':
      return 'Já há uma canalização a decorrer.';
    case 'destroyed':
      return 'A nave foi destruída.';
    case 'no_port':
      return 'Não há porto nessa direção.';
    case 'already_crossing':
      return 'Já há uma travessia em curso.';
    case 'in_port':
      return 'A nave já está num porto seguro: não há nada a canalizar daqui.';
  }
}

/** Porque é que uma travessia foi recusada. Exaustiva, como as outras. */
function crossingRefusalText(refusal: OpenWorldCrossingRefusal): string {
  switch (refusal) {
    case 'no_neighbour':
      return 'O mundo acaba nessa direção.';
    case 'safe_port':
      return 'Entrar num porto exige a canalização de dez segundos.';
    case 'not_at_border':
      return 'A nave não está na faixa dessa borda.';
    case 'already_crossing':
      return 'Já há uma travessia em curso.';
    case 'wrong_epoch':
      return 'A posição desta nave mudou. Tente outra vez.';
    case 'destroyed':
      return 'A nave foi destruída.';
    case 'destination_unavailable':
      return 'O quadrante de destino não respondeu.';
    case 'expired':
      return 'A reserva do destino expirou antes do compromisso.';
    case 'capacity':
      return 'QUADRANTE LOTADO — não há vaga disponível neste quadrante.';
    case 'unavailable':
      return 'O mundo aberto não conseguiu confirmar a travessia.';
  }
}

/** Porque é que uma canalização não chegou ao fim. */
function channelEndingText(ending: OpenWorldChannelEnding): string | null {
  switch (ending) {
    /* Tratado por quem chama: concluir não é uma notícia, é o que se pediu. */
    case 'completed':
      return null;
    case 'cancelled':
      return 'Canalização cancelada.';
    case 'moved':
      return 'A canalização parou: mexer a nave interrompe-a.';
    case 'damaged':
      return 'A canalização foi interrompida por dano.';
    case 'destroyed':
      return 'A nave foi destruída.';
    case 'left_border':
      return 'A nave saiu da faixa e a canalização parou.';
    case 'disconnected':
      return 'A ligação caiu e a canalização parou.';
    case 'no_destination':
      return 'O porto dessa direção deixou de existir.';
  }
}

export class OpenWorldConnection {
  private state: OpenWorldClientState = EMPTY;
  private readonly listeners = new Set<(state: OpenWorldClientState) => void>();
  /**
   * **Os corpos vivos, por identidade** (gate V28I.AK).
   *
   * Persistente entre tramas: o mapa da trama anterior JÁ é o desta, e
   * reconstruí-lo custava um tuplo por corpo, um array e um `Map` — vinte vezes
   * por segundo, sobre uma população que a mecânica aprovada leva a dezenas de
   * milhares.
   *
   * Uma alocação por entidade **viva**. Uma identidade que sai é apagada aqui, e
   * com ela o registo de apresentação inteiro.
   */
  private readonly liveShots = new Map<number, OpenWorldShot>();
  /**
   * **As formas de salva que esta ligação conhece** (protocolo 32). A sala guarda
   * a sua tabela por ligação e esquece-a numa ressincronização; aqui esquece-se
   * em `forgetShots`, que corre em cada descontinuidade — ressincronização,
   * reconexão, travessia e socket novo —, portanto os dois lados começam sempre
   * juntos do zero.
   */
  private readonly volleyShapes = new Map<number, readonly number[]>();
  /**
   * A lista que a cena consome, **refeita e não realocada** a cada trama.
   *
   * A cena precisa de uma lista; um array novo por trama era mais uma alocação
   * do tamanho da população. Este é esvaziado e cheio outra vez, e os elementos
   * são os registos persistentes.
   */
  private readonly shotList: OpenWorldShot[] = [];

  /**
   * Enche a lista publicada a partir dos corpos vivos, sem alocar.
   *
   * A ordem é a de inserção do mapa, que é a ordem por que a sala os anunciou —
   * a mesma que um array reconstruído dava, porque ele era construído do mesmo
   * mapa.
   */
  /**
   * **Esquece todos os corpos em voo** (gate V28I.AK).
   *
   * Chamado por tudo o que é descontinuidade — travessia, reconexão, troca de
   * quadrante e ressincronização — porque o mapa é agora PERSISTENTE: sem isto,
   * uma identidade do mundo anterior sobreviveria à mudança e voltaria a ser
   * desenhada na trama seguinte.
   *
   * Apagar o registo apaga a apresentação com ele: a cena deixa de ver o corpo,
   * devolve o sprite ao pool e nenhuma associação atravessa a fronteira.
   */
  private forgetShots(): void {
    this.liveShots.clear();
    this.volleyShapes.clear();
    this.shotList.length = 0;
  }

  private refillShots(): readonly OpenWorldShot[] {
    this.shotList.length = 0;
    for (const shot of this.liveShots.values()) {
      this.shotList.push(shot);
    }
    return this.shotList;
  }

  private socket: OpenWorldSocket | null = null;
  /**
   * Qual tentativa é dona do socket.
   *
   * Cada ligação sobe-a, e cada handler captura o valor sob o qual nasceu. Um
   * frame, um fecho ou um bilhete resolvido que pertença a uma tentativa
   * substituída é **descartado** — a mesma regra que o servidor aplica a uma
   * cadeira superseded, aplicada deste lado.
   */
  private generation = 0;
  private nextSeq = 1;
  /**
   * **Se esta ligação já é de alguém.**
   *
   * Verdadeira desde o início no caminho antigo, onde o upgrade já autorizou.
   * No caminho directo só depois do `accepted`: até lá o host fecha qualquer
   * coisa que não seja o `attach`, e enviar um comando seria perder o socket.
   */
  private attached = true;
  /**
   * A geração da cadeira, cunhada pela SALA e dita no `accepted`.
   *
   * Exposta porque é o que distingue "esta ligação" de "uma ligação que esta
   * conta teve": durante uma travessia existem duas de propósito, e uma
   * interface que quisesse dizer qual é a viva precisa deste número. O cliente
   * compara-o; nunca o propõe.
   */
  private hostGeneration: number | null = null;

  /** A geração que o host atribuiu a esta cadeira, ou null no caminho antigo. */
  get chairGeneration(): number | null {
    return this.hostGeneration;
  }
  /** A última intenção enviada, e quando. Ver `sendCommand`. */
  /**
   * **A última intenção ENVIADA, campo a campo** (gate V28I.AT).
   *
   * Era uma string montada por quadro para comparar cinco números. Agora são os
   * cinco números, e a comparação distingue o que é ESTADO — a mira, que pode
   * esperar pelo intervalo — do que é EVENTO — uma tecla ou um disparo, que
   * viaja na hora.
   *
   * `NaN` como valor inicial não casa com nada, nem consigo próprio, portanto a
   * primeira intenção de um socket é sempre enviada.
   */
  private lastMx = Number.NaN;
  private lastMy = Number.NaN;
  private lastAimX = Number.NaN;
  private lastAimY = Number.NaN;
  private lastFiring: boolean | null = null;
  private lastIntentAtMs = 0;
  /**
   * Quando cada sequência saiu, e a que instante local cada tique corresponde
   * para o próprio casco (gate V28I.BX.2). Ver `OpenWorldAckClock`.
   */
  private readonly ackClock = new OpenWorldAckClock(OPEN_WORLD_TICK_MS);
  private cancelRetry: (() => void) | null = null;
  /**
   * Quem apaga a última notícia.
   *
   * Uma recusa que ficasse no ecrã até à seguinte seria indistinguível de uma
   * recusa a repetir-se, e a pulsação da fronteira — uma trama por segundo —
   * apagá-la-ia antes de alguém a ler. Por isso é um prazo próprio: quatro
   * segundos, e o anterior é sempre cancelado para duas recusas seguidas não
   * deixarem a primeira a apagar a segunda.
   */
  private cancelNotice: (() => void) | null = null;
  private quadrant: OpenWorldCoordinate | null = null;

  /**
   * A ligação do DESTINO, aberta em segundo plano durante uma travessia.
   *
   * Existe ao mesmo tempo que a da origem e não desenha nada: a fonte de frames
   * só troca quando a origem fecha. É isso que faz a travessia não piscar e não
   * duplicar a nave.
   */
  private pending: {
    socket: OpenWorldSocket;
    quadrant: OpenWorldCoordinate;
    /** O bilhete por apresentar, quando o destino é um host directo. */
    attachTicket: string | null;
  } | null = null;

  constructor(private readonly deps: OpenWorldConnectionDeps) {}

  get snapshot(): OpenWorldClientState {
    return this.state;
  }

  subscribe(listener: (state: OpenWorldClientState) => void): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Abre a ligação a um quadrante. Um bilhete novo, sempre. */
  connect(quadrant: OpenWorldCoordinate, ticket?: OpenWorldTicketGrant): void {
    this.generation += 1;
    this.quadrant = quadrant;
    this.teardownSocket();
    this.patch({
      phase: 'authorising',
      quadrant,
      label: openWorldQuadrantLabel(quadrant),
      notice: null,
    });
    void this.authoriseAndOpen(this.generation, quadrant, ticket ?? null);
  }

  /** Fecha tudo. Uma expedição continua no servidor; o que acaba é esta janela. */
  disconnect(): void {
    this.generation += 1;
    this.teardownSocket();
    this.teardownPending();
    /* Fechar o modo esquece os corpos: nenhum deles sobrevive a uma reabertura. */
    this.forgetShots();
    /*
     * **O recibo vai-se com a ligação.**
     *
     * Ele ficava. `VOLTAR À CAÇA` desligava o socket e deixava o recibo no
     * estado — e como é ele que mantém a expedição "no ecrã", o botão não fazia
     * nada visível: o relatório continuava lá e a navegação da caça nunca
     * voltava. Dispensá-lo aqui não perde nada: o recibo é uma linha que já
     * existe no servidor, e uma reconexão volta a apresentá-lo.
     */
    this.patch({ phase: 'idle', notice: null, receipt: null, crossing: null });
  }

  /**
   * O comando deste instante. Um por frame, sem fila e sem repetição.
   *
   * Um comando que não pode sair é **largado** em vez de guardado: uma fila
   * entregaria uma rajada de direções velhas assim que a ligação voltasse, e o
   * jogador está a segurar o que está a segurar agora.
   */
  sendCommand(intent: {
    readonly mx: -1 | 0 | 1;
    readonly my: -1 | 0 | 1;
    readonly aimX: number;
    readonly aimY: number;
    readonly firing: boolean;
  }): void {
    if (this.state.phase !== 'live' && this.state.phase !== 'crossing') {
      return;
    }
    /*
     * **Uma intenção que não mudou não se repete sessenta vezes por segundo.**
     *
     * A cena chama isto a cada quadro, porque é a cada quadro que ela sabe o
     * que está a ser segurado. Mas a sala guarda a intenção como um **trinco**:
     * ela vale até chegar outra. Reenviá-la idêntica é pagar uma mensagem por
     * quadro para dizer a mesma coisa — e o orçamento da sala é noventa
     * comandos por segundo, portanto sessenta não deixavam margem nenhuma. Num
     * ecrã a 120 Hz, ou com duas fontes a falar pela mesma ligação, o limite
     * estourava e a sala descartava comandos: o resultado visível era uma nave
     * que ignorava o teclado.
     *
     * O batimento existe para o caso de uma mensagem se perder: sem ele, um
     * comando descartado deixaria a nave a andar — ou parada — até o jogador
     * mudar de tecla.
     */
    const now = this.deps.now();
    /*
     * **Uma TRANSIÇÃO viaja na hora** (gate V28I.AT).
     *
     * Uma tecla de movimento e o começo ou o fim de um disparo são eventos, e
     * perder um toque para poupar uma mensagem seria mudar o jogo. Só a MIRA é
     * estado, e só ela espera.
     */
    const moved = intent.mx !== this.lastMx || intent.my !== this.lastMy;
    const fired = intent.firing !== this.lastFiring;
    const aimed = intent.aimX !== this.lastAimX || intent.aimY !== this.lastAimY;
    if (!moved && !fired) {
      /*
       * **A mira sozinha respeita o intervalo da linha de base de 60 fps.**
       *
       * Esta cena emite uma intenção por QUADRO do Phaser, que não tem tecto, e
       * a mira é o vetor nave → ponteiro: ela muda a cada quadro enquanto a nave
       * anda, mesmo com o rato parado. Num monitor de 144 Hz isso eram 144
       * comandos por segundo contra o teto de 90 da sala — descartes silenciosos
       * e o socket fechado por `rate_limit`.
       *
       * A sala aplica a intenção e o TIQUE lê-a, portanto mandar mira mais
       * depressa do que ela tica não muda o que ela usa: isto é coalescência de
       * estado, e não perda de nada.
       */
      if (aimed && now - this.lastIntentAtMs < OPEN_WORLD_MIN_AIM_INTERVAL_MS) {
        return;
      }
      /* Nada mudou de todo: só o batimento, para uma mensagem perdida. */
      if (!aimed && now - this.lastIntentAtMs < INTENT_HEARTBEAT_MS) {
        return;
      }
    }
    this.lastMx = intent.mx;
    this.lastMy = intent.my;
    this.lastAimX = intent.aimX;
    this.lastAimY = intent.aimY;
    this.lastFiring = intent.firing;
    this.lastIntentAtMs = now;
    const seq = this.nextSeq;
    this.nextSeq += 1;
    if (moved) {
      /* Só uma transição de movimento alinha o casco previsto: ver `OpenWorldAckClock`. */
      this.stampSent(seq);
    }
    this.transmit({
      t: 'cmd',
      seq,
      mx: intent.mx,
      my: intent.my,
      ax: intent.aimX,
      ay: intent.aimY,
      f: intent.firing,
    });
  }

  /** Pede uma travessia por uma borda. Uma ordem, com a sua própria identidade. */
  requestCrossing(direction: OpenWorldDirection): string {
    const id = crypto.randomUUID();
    this.transmit({ t: 'x', seq: this.bumpSeq(), d: direction, id });
    return id;
  }

  /** Pede a entrada num porto vizinho. Dez segundos que o servidor conta. */
  requestPortEntry(direction: OpenWorldDirection): string {
    const id = crypto.randomUUID();
    this.transmit({ t: 'bp', seq: this.bumpSeq(), d: direction, id });
    return id;
  }

  /** Prepara uma extração. Dez segundos, e o desfecho é do servidor. */
  requestExtraction(): string {
    const id = crypto.randomUUID();
    this.transmit({ t: 'be', seq: this.bumpSeq(), id });
    return id;
  }

  /** Cancela o que estiver a canalizar. Sem id: cancelar não decide nada. */
  cancelBorder(): void {
    this.transmit({ t: 'bx', seq: this.bumpSeq() });
  }

  /**
   * **Lança a linha.** Uma intenção com identidade, e sem contador.
   *
   * Sem `seq` de propósito: a sala resolve as ordens de pesca antes do contador
   * de sequência, porque uma ORDEM traz a identidade dela e não pode ser
   * comparada com um número que não tem. Repetir o mesmo id não começa uma
   * segunda pescaria — responde o que já existia.
   *
   * Não nomeia água, peixe, espécie, raridade, peso nem dano: o servidor deriva
   * a água da posição autoritativa e escolhe o peixe deterministicamente.
   */
  startFishing(): string {
    const id = crypto.randomUUID();
    this.transmit({ t: 'fs', id });
    return id;
  }

  /**
   * **Abandona a pescaria**, e o peixe escapa.
   *
   * Leva o id da tentativa — o mesmo que o servidor publica em `fm.o` — porque
   * abandonar deixou de não decidir nada: um segundo clique atrasado, ou uma
   * ordem repetida pela rede, mataria a pescaria SEGUINTE se a ordem não dissesse
   * qual abandona. Um id que não é o da tentativa a decorrer não faz nada.
   */
  abandonFishing(orderId: string): void {
    this.transmit({ t: 'fx', id: orderId });
  }

  /**
   * **Abre uma caixa.**
   *
   * O navegador diz QUAL caixa quer abrir — e mais nada. Ele não afirma
   * distância, propriedade, conteúdo, capacidade, quantidade, Ouro nem sucesso:
   * o servidor relê a posição da nave e valida o id que recebeu.
   */
  openBox(boxId: string): string {
    const id = crypto.randomUUID();
    const frame: OpenWorldClientFrame = { t: 'lo', seq: this.bumpSeq(), id, b: boxId };
    this.transmit(frame);
    return id;
  }

  /** Fecha. Sem id de ordem: fechar não decide nada. */
  closeBox(): void {
    const frame: OpenWorldClientFrame = { t: 'lc', seq: this.bumpSeq() };
    this.transmit(frame);
  }

  /** Pega UM item. Uma ordem, com identidade. */
  takeItem(boxId: string, fieldItemId: string): string {
    const id = crypto.randomUUID();
    const frame: OpenWorldClientFrame = {
      t: 'lt',
      seq: this.bumpSeq(),
      id,
      b: boxId,
      f: fieldItemId,
    };
    this.transmit(frame);
    return id;
  }

  /**
   * **Pega todos os ITENS.** O Ouro tem ordem própria e não vem nesta.
   *
   * O nome do botão diz exactamente isso, porque a semântica do servidor é essa
   * — renomeá-la aqui seria mentir sobre o que o clique faz.
   */
  takeAllItems(boxId: string): string {
    const id = crypto.randomUUID();
    const frame: OpenWorldClientFrame = { t: 'la', seq: this.bumpSeq(), id, b: boxId };
    this.transmit(frame);
    return id;
  }

  /** Pega o Ouro. Ele não ocupa espaço, e por isso é uma ordem à parte. */
  takeGold(boxId: string): string {
    const id = crypto.randomUUID();
    const frame: OpenWorldClientFrame = { t: 'lg', seq: this.bumpSeq(), id, b: boxId };
    this.transmit(frame);
    return id;
  }

  /**
   * **Liga a carta deste espaço.**
   *
   * O navegador diz QUAL espaço, e mais nada: não há campo para dono, nível,
   * arrefecimento, alvo, duração de carga, dano nem resultado. Para as duas que
   * carregam, isto começa a carga — e o servidor conta-a pelo relógio dele.
   */
  activateCard(slot: 1 | 2 | 3): string {
    const id = crypto.randomUUID();
    const frame: OpenWorldClientFrame = { t: 'ka', seq: this.bumpSeq(), id, s: slot };
    this.transmit(frame);
    return id;
  }

  /** **Solta a carga deste espaço.** A segunda metade do mesmo gesto. */
  releaseCard(slot: 1 | 2 | 3): string {
    const id = crypto.randomUUID();
    const frame: OpenWorldClientFrame = { t: 'kr', seq: this.bumpSeq(), id, s: slot };
    this.transmit(frame);
    return id;
  }

  /**
   * **Pede para pilotar um Leviatã.**
   *
   * Não nomeia a estrutura: o servidor relê a posição do casco e escolhe a que
   * está ao alcance. Um pedido feito longe é gasto na tentativa.
   */
  boardSummon(): string {
    const id = crypto.randomUUID();
    const frame: OpenWorldClientFrame = { t: 'kb', seq: this.bumpSeq(), id };
    this.transmit(frame);
    return id;
  }

  /** Pede a lista da própria carga. Perguntar não decide nada. */
  inspectCargo(): void {
    const frame: OpenWorldClientFrame = { t: 'cq' };
    this.transmit(frame);
  }

  /** Pede o balcão. Perguntar não decide nada, e repetir é o mesmo. */
  openMerchant(): void {
    this.transmit({ t: 'mo' });
  }

  /**
   * **Vende.** Uma intenção com identidade, e mais nada.
   *
   * `ids` nomeia linhas; `null` é o VENDER TODOS, e nesse caso o cliente não
   * envia lista nenhuma — quem determina as elegíveis é a autoridade. Nunca
   * viaja preço, raridade, peso, multiplicador ou quantidade.
   */
  sell(scope: 'field' | 'vault', ids: readonly string[] | null): string {
    const id = crypto.randomUUID();
    this.transmit({ t: 'ms', id, sc: scope, ids: ids === null ? null : [...ids] });
    return id;
  }

  /**
   * As caixas visíveis, depois de entrarem e saírem.
   *
   * Uma marca que volta com uma assinatura nova substitui a anterior; uma que
   * sai desaparece. É a mesma disciplina das rochas, e é o que faz uma caixa
   * esvaziada ou expirada desaparecer sem nada de especial.
   */
  private mergeBoxes(
    entered: readonly OpenWorldBoxMark[],
    left: readonly string[],
  ): readonly OpenWorldBoxMark[] {
    const boxes = new Map(this.state.boxes.map((box) => [box.i, box]));
    for (const box of entered) {
      boxes.set(box.i, box);
    }
    for (const id of left) {
      boxes.delete(id);
    }
    return [...boxes.values()];
  }

  private bumpSeq(): number {
    const seq = this.nextSeq;
    this.nextSeq += 1;
    return seq;
  }

  /** O relógio de apresentação: o da página quando é dado, o de sempre quando não. */
  private presentationNow(): number {
    return (this.deps.presentationNow ?? this.deps.now)();
  }

  /** Guarda quando esta sequência saiu, no relógio de apresentação. */
  private stampSent(seq: number): void {
    this.ackClock.sent(seq, this.presentationNow());
  }

  /** Um socket novo recomeça a contagem: nenhum reconhecimento velho vale nele. */
  private forgetAcks(): void {
    this.ackClock.reset();
  }

  /* ------------------------------------------------------------------ */
  /*  A ligação                                                          */
  /* ------------------------------------------------------------------ */

  private async authoriseAndOpen(
    generation: number,
    quadrant: OpenWorldCoordinate,
    presented: OpenWorldTicketGrant | null,
  ): Promise<void> {
    const grant = presented ?? (await this.deps.mintTicket(quadrant).catch(() => null));
    if (generation !== this.generation) {
      /* Uma tentativa substituída. O bilhete que chegou tarde não senta ninguém. */
      return;
    }
    if (grant === null) {
      this.patch({ phase: 'failed', notice: 'Não foi possível autorizar a ligação.' });
      return;
    }
    this.patch({ phase: 'connecting' });
    /*
     * **Os dois caminhos, escolhidos pelo servidor e nunca aqui.**
     *
     * Sem destino: o Durable Object da mesma origem, com o bilhete na query,
     * exatamente como sempre. Com destino: um host de batalha, e o bilhete
     * viaja na primeira mensagem — nunca na URL, que entra em logs de proxy,
     * no histórico e nas métricas por rota.
     */
    const destination = grant.socketUrl;
    const socket = this.deps.openSocket(
      destination === null ? this.deps.socketUrl(grant.ticket) : destination,
    );
    const direct = destination !== null;
    this.socket = socket;
    this.attached = !direct;
    this.bind(socket, generation, direct ? grant.ticket : null);
  }

  private bind(socket: OpenWorldSocket, generation: number, attachTicket: string | null): void {
    /*
     * **Cada socket começa sem trinco.** A deduplicação de `sendCommand` só é
     * correta contra a sala que recebeu o último comando; uma reconexão fala
     * com uma cadeira que não o viu, e reenviar "igual ao anterior" deixaria a
     * nave parada até o jogador largar a tecla.
     */
    this.lastMx = Number.NaN;
    this.lastMy = Number.NaN;
    this.lastAimX = Number.NaN;
    this.lastAimY = Number.NaN;
    this.lastFiring = null;
    socket.onOpen(() => {
      if (generation !== this.generation) {
        return;
      }
      this.nextSeq = 1;
      this.forgetAcks();
      if (attachTicket === null) {
        /* Caminho antigo: o upgrade já autorizou, e o socket nasce vivo. */
        this.patch({ phase: 'live', notice: null });
        return;
      }
      /*
       * Caminho directo: o socket está aberto e **ainda não é de ninguém**. Até
       * o `accepted` chegar não se envia um comando, porque o host fecha
       * qualquer coisa que não seja o `attach`.
       */
      socket.send(JSON.stringify({ t: 'ta', tv: BATTLE_TRANSPORT_VERSION, ticket: attachTicket }));
    });
    socket.onMessage((data) => {
      if (generation !== this.generation) {
        /* Um frame de uma ligação substituída. Descartado, e nunca desenhado. */
        return;
      }
      if (attachTicket !== null && this.receiveTransport(data)) {
        return;
      }
      this.receive(data);
    });
    socket.onClose(() => {
      if (generation !== this.generation) {
        return;
      }
      this.onDrop();
    });
    socket.onError(() => {
      if (generation !== this.generation) {
        return;
      }
      this.onDrop();
    });
  }

  /**
   * O socket caiu.
   *
   * Uma expedição **não acaba** por isso: o casco fica no quadrante, sólido e
   * vulnerável, durante três minutos. O que este cliente faz é voltar a
   * autorizar-se e a abrir — com um bilhete novo, porque o anterior foi gasto.
   */
  private onDrop(): void {
    if (this.state.phase === 'ended') {
      return;
    }
    this.socket = null;
    const quadrant = this.quadrant;
    if (quadrant === null) {
      this.patch({ phase: 'idle' });
      return;
    }
    this.patch({ phase: 'reconnecting', notice: 'Ligação perdida. A reconectar…' });
    this.cancelRetry?.();
    this.cancelRetry = this.deps.schedule(() => {
      if (this.state.phase !== 'reconnecting') {
        return;
      }
      this.connect(quadrant);
    }, RECONNECT_DELAY_MS);
  }

  /**
   * **As tramas de TRANSPORTE, que a sala não conhece.**
   *
   * Devolve `true` quando consumiu a mensagem. Só o caminho directo passa por
   * aqui: no caminho antigo não existe nenhuma destas, e tentar lê-las seria
   * inventar um protocolo onde não há um.
   *
   * Depois do `accepted` o fluxo **é** o protocolo de sempre. Não há embrulho,
   * não há tradução, e o `t` de uma trama de transporte começa por `t` — o que
   * torna uma colisão com o jogo impossível em vez de improvável.
   */
  private receiveTransport(raw: string): boolean {
    let payload: unknown;
    try {
      payload = JSON.parse(raw);
    } catch {
      return false;
    }
    const parsed = battleTransportServerFrameSchema.safeParse(payload);
    if (!parsed.success) {
      return false;
    }
    const frame = parsed.data;
    switch (frame.t) {
      case 'to':
        /*
         * A cadeira é nossa. A geração vem do servidor: com duas ligações vivas
         * durante uma travessia, é o que permite largar o que veio de uma
         * cadeira que já não é esta.
         */
        this.attached = true;
        this.hostGeneration = frame.gen;
        this.patch({ phase: 'live', notice: null });
        return true;
      case 'th':
        /* Prova que o socket está vivo. Respondida, e não avança nada. */
        this.socket?.send(JSON.stringify({ t: 'th' }));
        return true;
      case 'td':
        /*
         * A máquina vai-se embora. Não é um facto do jogo, portanto não é uma
         * razão de fecho: o casco fica no quadrante e o que muda é o fio.
         */
        this.patch({ phase: 'reconnecting', notice: 'A mudar de servidor…' });
        return true;
      case 'tr':
        this.reconnectSoon('A mudar de servidor…');
        return true;
      case 'tx':
        this.onTransportRefusal(frame.c);
        return true;
      default:
        return false;
    }
  }

  /**
   * O que fazer com uma recusa do transporte.
   *
   * Recuperável: voltar com um bilhete novo. Terminal: parar, porque insistir
   * com o mesmo estado dá o mesmo resultado e esconde a razão ao jogador.
   *
   * **Nunca recuar para o Durable Object a meio de uma sessão.** Depois do
   * `accepted` a cadeira existe no host e o casco está na simulação dele; abrir
   * a sala antiga passaria a haver duas autoridades sobre o mesmo casco, que é
   * exatamente o que a vedação por época existe para tornar impossível.
   */
  private onTransportRefusal(refusal: BattleTransportRefusal): void {
    if (isRecoverableBattleRefusal(refusal)) {
      this.reconnectSoon(
        refusal === 'full' ? 'O quadrante está cheio. A tentar outra vez…' : 'A reconectar…',
      );
      return;
    }
    this.patch({
      phase: 'failed',
      notice:
        refusal === 'version'
          ? 'Esta versão do jogo já não é aceite. Recarregue a página.'
          : 'A ligação foi recusada.',
    });
  }

  /** Marcar reconexão e agendar uma tentativa, com um bilhete novo. */
  private reconnectSoon(notice: string): void {
    const quadrant = this.quadrant;
    if (quadrant === null) {
      this.patch({ phase: 'idle' });
      return;
    }
    this.patch({ phase: 'reconnecting', notice });
    this.cancelRetry?.();
    this.cancelRetry = this.deps.schedule(() => {
      if (this.state.phase !== 'reconnecting') {
        return;
      }
      this.connect(quadrant);
    }, RECONNECT_DELAY_MS);
  }

  private receive(raw: string): void {
    let payload: unknown;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    const parsed = openWorldServerFrameSchema.safeParse(payload);
    if (!parsed.success) {
      return;
    }
    this.apply(parsed.data);
  }

  private apply(frame: OpenWorldServerFrame): void {
    switch (frame.t) {
      case 'w':
        this.patch({
          quadrant: frame.q,
          label: openWorldQuadrantLabel(frame.q),
          sideMu: frame.side,
          me: frame.me,
          phase: 'live',
        });
        return;
      case 's':
        this.applyState(frame);
        return;
      case 'bs':
        this.patch({ border: frame });
        return;
      case 'cg':
        this.patch({ cargo: frame });
        return;
      case 'xt':
        if (frame.state === 'failed_before_commit') {
          /*
           * **Uma extração recusada antes do compromisso** (gate V28I.BY). A sala
           * não o dizia, e o piloto ficava com "Extração pronta." e mais nada.
           * Nada mudou: ele continua a voar com tudo o que trazia, e a razão é
           * dita.
           */
          this.flash(extractionRefusalText(frame.r));
          return;
        }
        /*
         * A expedição acabou. O casco já não é deste mundo, e o que resta é
         * mostrar o recibo e voltar à caça.
         */
        this.patch({ receipt: frame, phase: 'ended', notice: null });
        return;
      case 'xr':
        this.applyCrossing(frame);
        return;
      case 'e':
        /*
         * **Uma recusa da sala, e ela tem de se ver.**
         *
         * Isto caía no `default` e desaparecia: o jogador carregava, nada
         * acontecia, e não havia como distinguir uma recusa de um clique que
         * não registou. Um código, quatro frases, e nada mais — a forma do
         * frame não mudou, só passou a ter quem a leia.
         *
         * Uma trama de uma ligação substituída nunca chega aqui: `bind` já a
         * descarta pela geração, antes de `receive`.
         */
        this.flash(errorText(frame.c));
        return;
      case 'c':
        /*
         * `departed` é a única razão que não é um fim: o casco está noutro
         * quadrante, e a ligação do destino já está aberta à espera.
         */
        if (frame.c === 'departed') {
          this.swapToPending();
          return;
        }
        this.patch({
          phase: this.state.receipt === null ? 'failed' : 'ended',
          notice: frame.c === 'destroyed' ? 'A nave foi destruída.' : null,
        });
        return;
      case 'lb':
        /*
         * **O interior de uma caixa**, e só a quem a abriu.
         *
         * Substituído por inteiro: a trama é uma fotografia, e a revisão que ela
         * traz é como dois saqueadores no mesmo baú convergem para o que resta.
         */
        this.patch({ boxView: frame, lootResult: null });
        return;
      case 'lr':
        /*
         * **O que uma ordem de saque produziu.** A interface só muda agora —
         * nada foi retirado da lista antes de a autoridade o dizer.
         */
        this.patch({ lootResult: frame });
        return;
      case 'lx':
        /* A interação acabou. O painel fecha, e a razão é dita. */
        this.patch({ boxView: null, lootResult: null });
        this.flash(lootEndText(frame.c));
        return;
      case 'ci':
        this.patch({ cargoItems: frame.i });
        return;
      case 'kc':
        /*
         * **Os meus três espaços.** Só chega quando muda, e a mudança é medida
         * ao segundo pela sala: um anel que recebesse o mesmo número vinte
         * vezes por segundo desenharia a mesma coisa vinte vezes.
         */
        this.patch({ cardSlots: frame.k });
        return;
      case 'mv':
        /* O balcão, e só a mim. Substitui o anterior por inteiro. */
        this.patch({ merchantView: frame, saleRefusal: null });
        return;
      case 'mr':
        /*
         * **O recibo.** A interface só muda agora: nada foi alterado
         * otimisticamente enquanto a ordem estava em voo.
         */
        this.patch({ saleReceipt: frame, saleRefusal: null });
        return;
      case 'mf':
        this.patch({ saleRefusal: frame, saleReceipt: null });
        return;
      case 'fr':
        /*
         * **Como a minha pescaria acabou.** Uma vez, e só a mim.
         *
         * O resultado fica no estado para o painel o mostrar; o `fishing` é
         * limpo pela mesma trama que traz o `fm: null`, portanto não há aqui
         * nada a fechar à mão.
         */
        this.patch({ fishingResult: frame, fishingRefusal: null });
        return;
      case 'ff':
        /* Uma ordem recusada tem de se ver, como as da fronteira. */
        this.patch({ fishingRefusal: frame, fishingResult: null });
        return;
      case 'bf':
        /* Ver `flash`: uma recusa lê-se e desaparece sozinha. */
        /*
         * **Uma ordem recusada tem de se ver.**
         *
         * Isto caía no `default` e desaparecia: o piloto carregava num botão da
         * fronteira, a sala recusava com uma razão exata, e o ecrã não dizia
         * nada — indistinguível de um clique que não registou.
         */
        this.flash(borderRefusalText(frame.r));
        return;
      case 'bc':
        /* Como acabou a canalização. `completed` fala por si: o que se pediu acontece. */
        this.flash(
          frame.e === 'completed'
            ? frame.ready
              ? 'Extração pronta.'
              : null
            : channelEndingText(frame.e),
        );
        return;
      default:
        return;
    }
  }

  /**
   * Mostra uma frase curta e apaga-a sozinha. `null` não mostra nada.
   *
   * O prazo vem de `deps.schedule`, o mesmo que a reconexão usa, portanto uma
   * bancada com relógio injetado controla os dois da mesma maneira.
   */
  private flash(notice: string | null): void {
    this.cancelNotice?.();
    this.cancelNotice = null;
    if (notice === null) {
      this.patch({ notice: null });
      return;
    }
    this.patch({ notice });
    const generation = this.generation;
    this.cancelNotice = this.deps.schedule(() => {
      this.cancelNotice = null;
      /* Uma notícia de uma tentativa substituída não apaga a da atual. */
      if (this.generation === generation && this.state.notice === notice) {
        this.patch({ notice: null });
      }
    }, NOTICE_MS);
  }

  private applyState(frame: OpenWorldStateFrame): void {
    /* Os carimbos de apresentação (gate V28I.BX.2): a chegada, e o alinhamento do próprio casco. */
    const receivedAt = this.presentationNow();
    const ownSyncAt = this.ackClock.acknowledged(frame.a, frame.k);
    const entities: OpenWorldEntity[] = [];
    const seen = new Map(this.state.entities.map((entity) => [entity.id, entity]));
    if (frame.r === 1) {
      seen.clear();
    }
    for (const left of frame.x) {
      seen.delete(left);
    }
    const own = frame.me;
    const remember = (entry: (typeof frame.e)[number]) => {
      seen.set(entry.s.i, {
        id: entry.s.i,
        x: entry.s.x,
        y: entry.s.y,
        aimX: entry.s.ax,
        aimY: entry.s.ay,
        moveX: entry.s.mx,
        moveY: entry.s.my,
        /*
         * **A vida atual vem do estado; o máximo vem da entrada.**
         *
         * São dois campos porque são duas coisas com dois ritmos: `hm` é o
         * denominador, dito uma vez quando o casco entra na área de interesse
         * porque um perfil congelado não muda; `s.h` é o numerador, e chega
         * vinte vezes por segundo porque é o que se altera.
         */
        healthMilli: entry.s.h,
        maxHealthMilli: entry.hm,
        radiusMu: entry.r,
        nickname: entry.n,
        appearance: entry.a,
        connected: entry.s.c,
        /* Dito na ENTRADA: uma composição é congelada, e não muda com a cadeira. */
        ally: entry.al === 1,
        cards: entityCardsOf(entry.s),
      });
    };
    for (const entry of frame.e) {
      remember(entry);
    }
    remember(own);
    for (const update of frame.u) {
      const existing = seen.get(update.i);
      if (existing === undefined) {
        continue;
      }
      seen.set(update.i, {
        ...existing,
        x: update.x,
        y: update.y,
        aimX: update.ax,
        aimY: update.ay,
        moveX: update.mx,
        moveY: update.my,
        /* O numerador outra vez: sem ele, uma barra nunca desceria. */
        healthMilli: update.h,
        connected: update.c,
        /*
         * **As cartas vêm SEMPRE, e substituem por inteiro.**
         *
         * Um estado que só trouxesse as bandeiras acesas obrigaria o cliente a
         * adivinhar quando uma se apagou, e um congelamento que acabou ficaria
         * desenhado para sempre. A ausência de um campo é "não", e é por isso
         * que a leitura é total.
         */
        cards: entityCardsOf(update),
      });
    }
    for (const entity of seen.values()) {
      entities.push(entity);
    }

    /*
     * **As invocações: entram inteiras e saem por id.**
     *
     * Sem canal de mudou: elas movem-se em todos os tiques em que existem,
     * portanto a entrada é reenviada sempre e não haveria o que poupar. Uma
     * ressincronização limpa o mapa, como faz com tudo o resto.
     */
    const summons = new Map(this.state.summons.map((summon) => [summon.i, summon]));
    if (frame.r === 1) {
      summons.clear();
    }
    for (const entry of frame.sm ?? []) {
      summons.set(entry.i, entry);
    }
    for (const gone of frame.sr ?? []) {
      summons.delete(gone);
    }

    /*
     * **As rochas: entram inteiras, mudam por estado, saem por id.**
     *
     * A mesma disciplina dos cascos, e pela mesma razão: o que não muda é dito
     * uma vez. Uma rocha parada não vem na trama, portanto o mapa anterior é a
     * fonte para tudo o que não foi mencionado.
     */
    const comets = new Map(this.state.comets.map((comet) => [comet.id, comet]));
    if (frame.r === 1) {
      comets.clear();
    }
    for (const entry of frame.ce ?? []) {
      comets.set(entry.i, {
        id: entry.i,
        generation: entry.g,
        x: entry.x,
        y: entry.y,
        radiusMu: entry.r,
        parts: entry.p,
        visualSeed: entry.v,
        mask: 0,
        coreExposed: false,
        coreHealth: 0,
      });
    }
    for (const update of frame.cu ?? []) {
      const existing = comets.get(update.i);
      if (existing === undefined) {
        continue;
      }
      comets.set(update.i, {
        ...existing,
        mask: update.m,
        coreExposed: update.c,
        coreHealth: update.ch,
      });
    }
    for (const gone of frame.cx ?? []) {
      comets.delete(gone);
    }

    /* Os planetas, na mesma disciplina das rochas: entram, mudam, saem. */
    const planets = new Map(this.state.planets.map((planet) => [planet.id, planet]));
    if (frame.r === 1) {
      planets.clear();
    }
    for (const entry of frame.pe2 ?? []) {
      planets.set(entry.i, {
        id: entry.i,
        generation: entry.g,
        x: entry.x,
        y: entry.y,
        radiusMu: entry.r,
        parts: entry.p,
        visualSeed: entry.v,
        mask: 0,
        cores: [],
      });
    }
    for (const update of frame.pu2 ?? []) {
      const existing = planets.get(update.i);
      if (existing === undefined) {
        continue;
      }
      planets.set(update.i, {
        ...existing,
        mask: update.m,
        cores: update.c.map((core) => ({ index: core.c, health: core.h })),
      });
    }
    for (const gone of frame.px2 ?? []) {
      planets.delete(gone);
    }

    /*
     * Os mobs, na mesma disciplina. A entrada traz o que nao muda — a especie,
     * o raio, a vida maxima — e a atualizacao traz o que muda.
     */
    const mobs = new Map(this.state.mobs.map((mob) => [mob.id, mob]));
    if (frame.r === 1) {
      mobs.clear();
    }
    for (const entry of frame.me2 ?? []) {
      mobs.set(entry.i, {
        id: entry.i,
        generation: entry.g,
        mapIndex: entry.m,
        x: 0,
        y: 0,
        dirX: 1,
        dirY: 0,
        radiusMu: entry.r,
        health: BigInt(entry.hm),
        maxHealth: BigInt(entry.hm),
        stance: 'idle',
        poisoned: false,
        bleeding: false,
        /* Uma entrada descreve o corpo; o que lhe está a ser feito vem no estado. */
        cards: creatureCardsOf({}),
      });
    }
    for (const update of frame.mu ?? []) {
      const existing = mobs.get(update.i);
      if (existing === undefined) {
        continue;
      }
      mobs.set(update.i, {
        ...existing,
        x: update.x,
        y: update.y,
        dirX: update.dx,
        dirY: update.dy,
        health: BigInt(update.h),
        stance: update.s,
        poisoned: update.p,
        bleeding: update.b,
        /*
         * **As penalidades vêm SEMPRE, e substituem por inteiro.**
         *
         * Um estado que só trouxesse as bandeiras acesas obrigaria o cliente a
         * adivinhar quando uma se apagou, e um congelamento que acabou ficaria
         * desenhado para sempre. A ausência de um campo é "não".
         */
        cards: creatureCardsOf(update),
      });
    }
    for (const gone of frame.mx ?? []) {
      mobs.delete(gone);
    }

    /* Os bosses, na mesma disciplina: entram, mudam, saem. */
    const bosses = new Map(this.state.bosses.map((boss) => [boss.id, boss]));
    if (frame.r === 1) {
      bosses.clear();
    }
    for (const entry of frame.be ?? []) {
      bosses.set(entry.i, {
        id: entry.i,
        generation: entry.g,
        mapIndex: entry.m,
        role: entry.r,
        scaleBps: entry.s,
        x: 0,
        y: 0,
        dirX: 1,
        dirY: 0,
        radiusMu: entry.rd,
        health: BigInt(entry.hm),
        maxHealth: BigInt(entry.hm),
        poisoned: false,
        bleeding: false,
        /* Uma entrada descreve o corpo; o que lhe está a ser feito vem no estado. */
        cards: creatureCardsOf({}),
        strike: null,
        ultimate: null,
      });
    }
    for (const update of frame.bu ?? []) {
      const existing = bosses.get(update.i);
      if (existing === undefined) {
        continue;
      }
      bosses.set(update.i, {
        ...existing,
        x: update.x,
        y: update.y,
        dirX: update.dx,
        dirY: update.dy,
        health: BigInt(update.h),
        poisoned: update.p,
        bleeding: update.b,
        /* Substituídas por inteiro: a ausência de um campo é "não". */
        cards: creatureCardsOf(update),
        strike: update.s,
        ultimate: update.u,
      });
    }
    for (const gone of frame.bx ?? []) {
      bosses.delete(gone);
    }

    /*
     * **Os teleguiados e os núcleos** (gate V28I.BG).
     *
     * A sala manda o estado INTEIRO de cada um em cada pulsação em que ele está
     * à vista, porque os dois mudam em cada tique. O mapa mantém-se entre
     * tramas para uma pulsação em que nada esteja visível não os apagar — o que
     * sai vem em `bhx` e `bkx`, e é a sala que o diz.
     */
    const bossHoming = new Map(this.state.bossHoming.map((one) => [one.id, one]));
    const bossCores = new Map(this.state.bossCores.map((one) => [one.id, one]));
    if (frame.r === 1) {
      bossHoming.clear();
      bossCores.clear();
    }
    for (const entry of frame.bh ?? []) {
      bossHoming.set(entry.i, {
        id: entry.i,
        targetEntityId: entry.o,
        ability: entry.k,
        x: entry.x,
        y: entry.y,
        dirX: entry.dx,
        dirY: entry.dy,
        radiusMu: entry.r,
      });
    }
    for (const gone of frame.bhx ?? []) {
      bossHoming.delete(gone);
    }
    for (const entry of frame.bk ?? []) {
      bossCores.set(entry.i, {
        id: entry.i,
        bossId: entry.b,
        x: entry.x,
        y: entry.y,
        radiusMu: entry.r,
        health: BigInt(entry.h),
        maxHealth: BigInt(entry.hm),
      });
    }
    for (const gone of frame.bkx ?? []) {
      bossCores.delete(gone);
    }

    /*
     * As brasas: entram e saem, e nunca mudam. A mesma disciplina de uma água, e
     * pela mesma razão — um lugar não se mexe, portanto não há atualização.
     */
    const embers = new Map(this.state.embers.map((ember) => [ember.id, ember]));
    if (frame.r === 1) {
      embers.clear();
    }
    for (const entry of frame.bm ?? []) {
      embers.set(entry.i, {
        id: entry.i,
        bossId: entry.b,
        x: entry.x,
        y: entry.y,
        radiusMu: entry.r,
        shape: entry.sh,
        dirX: entry.dx,
        dirY: entry.dy,
        sectorTanBps: entry.an,
        slowBps: entry.sl,
        bornAtTick: entry.bt,
        expiresAtTick: entry.et,
      });
    }
    for (const gone of frame.bmx ?? []) {
      embers.delete(gone);
    }

    /* As águas: entram e saem, e nunca mudam. Um lugar não se mexe. */
    const waters = new Map(this.state.waters.map((water) => [water.id, water]));
    if (frame.r === 1) {
      waters.clear();
    }
    for (const entry of frame.wa ?? []) {
      waters.set(entry.i, {
        id: entry.i,
        x: entry.x,
        y: entry.y,
        radiusMu: entry.r,
        visualSeed: entry.v,
      });
    }
    for (const gone of frame.wx ?? []) {
      waters.delete(gone);
    }

    /*
     * **O mapa dos corpos é PERSISTENTE** (gate V28I.AK).
     *
     * Isto reconstruía-o a cada trama a partir do array que a trama anterior
     * tinha produzido — um tuplo `[id, corpo]` por corpo, mais um array, mais
     * um `Map`, vinte vezes por segundo. Com dezenas de milhares de corpos em
     * voo era trabalho por nada: o mapa da trama anterior já era este.
     */
    const shots = this.liveShots;
    if (frame.r === 1) {
      /* Uma ressincronização descreve o mundo de novo: o que havia não conta. */
      this.forgetShots();
    }
    for (const gone of frame.px) {
      shots.delete(gone);
    }
    /*
     * **O ponto ÚNICO de expansão** (gate V28I.AI).
     *
     * Um lote é transporte, e desfaz-se aqui: a partir desta linha o resto do
     * sistema — este ciclo, a ponte e a cena — vê exatamente a mesma lista de
     * corpos individuais que veria se cada um tivesse viajado no seu próprio
     * `pe`. Vinte e oito corpos continuam a ser vinte e oito registos, vinte e
     * oito sprites, vinte e oito rastos e vinte e oito impactos.
     *
     * A cena **não sabe** de onde eles vieram: de várias entradas, de um lote,
     * de uma ressincronização ou de uma chegada tardia à área de interesse. É
     * essa ignorância que faz o diff visual deste gate ser zero.
     *
     * A expansão é a função canónica do contrato partilhado, e não uma segunda
     * escrita: uma fórmula aqui e outra no servidor seriam duas respostas à
     * mesma pergunta.
     */
    /*
     * **Um lote é PERCORRIDO, e não materializado** (gate V28I.AP).
     *
     * Isto era `[...pb.flatMap(expandir), ...pe]`: um objeto por corpo mais dois
     * arrays, construídos e deitados fora no mesmo passo em que eram lidos —
     * até vinte e oito objetos por lote, por trama. O percurso entrega os mesmos
     * campos, pela mesma ordem, sem alocar nenhum deles, e a fórmula continua a
     * ser a canónica do contrato partilhado.
     */
    for (const batch of frame.pb ?? []) {
      openWorldForEachProjectileInBatch(batch, (id, x, y, vx, vy, k, o, cb, br, linear) => {
        shots.set(id, {
          base: {
            id,
            owner: o,
            kind: k ?? 'shot',
            /* Comum ao lote: os corpos nasceram do mesmo casco no mesmo instante. */
            scaleBps: batch.sc ?? OPEN_WORLD_BASE_PROJECTILE_SCALE_BPS,
            vx,
            vy,
            linear,
            baseX: x,
            baseY: y,
            baseTick: frame.k,
            card: cb ?? null,
            blastMu: br ?? null,
          },
          x,
          y,
        });
      });
    }
    /*
     * **Uma salva é expandida aqui, pela função canónica** (protocolo 32). Cada
     * corpo continua a ser um registo com a sua identidade e a sua posição,
     * exatas — a sala calculou as diferenças, o cliente só soma. Um corpo de
     * salva é sempre um tiro linear, sem carta e sem explosão.
     */
    for (const volley of frame.pv ?? []) {
      const scaleBps = volley.sc ?? OPEN_WORLD_BASE_PROJECTILE_SCALE_BPS;
      openWorldForEachProjectileInVolley(volley, this.volleyShapes, (id, x, y) => {
        shots.set(id, {
          base: {
            id,
            owner: volley.o,
            kind: 'shot',
            scaleBps,
            vx: volley.vx,
            vy: volley.vy,
            linear: true,
            baseX: x,
            baseY: y,
            baseTick: frame.k,
            card: null,
            blastMu: null,
          },
          x,
          y,
        });
      });
    }
    for (const entered of frame.pe) {
      shots.set(entered.i, {
        base: {
          id: entered.i,
          owner: entered.o,
          kind: entered.k ?? 'shot',
          scaleBps: entered.sc ?? OPEN_WORLD_BASE_PROJECTILE_SCALE_BPS,
          vx: entered.vx,
          vy: entered.vy,
          /*
           * **A marca vem da sala, e nunca é deduzida** (gate V28I.Z.3).
           *
           * Ausente quer dizer "espera pelo `pu`", que é o significado de sempre:
           * um corpo que curva, um que possa ser corrigido e qualquer forma que a
           * sala ainda não tenha classificado continuam a chegar em cada trama.
           */
          linear: entered.l === 1,
          baseX: entered.x,
          baseY: entered.y,
          baseTick: frame.k,
          /*
           * **Qual corpo de carta ele é**, dito na ENTRADA e nunca deduzido.
           *
           * Uma Esfera, um Catolé, um dardo e um raio desenham-se de maneiras
           * diferentes, e adivinhá-lo pelo tamanho seria a cena a decidir a arte a
           * partir de um número de equilíbrio. Ausente é um tiro comum.
           */
          card: entered.cb ?? null,
          blastMu: entered.br ?? null,
          ...(entered.tx === undefined || entered.ty === undefined
            ? {}
            : { targetX: entered.tx, targetY: entered.ty }),
        },
        /* A posição desenhada nasce onde a sala disse, e move-se daí. */
        x: entered.x,
        y: entered.y,
      });
    }
    /*
     * **Uma atualização ESCREVE no corpo que já existe** (gate V28I.AP).
     *
     * Isto construía um `base` novo e um `OpenWorldShot` novo por corpo e por
     * trama, só para trocar cinco números — e voltava a pô-los no mapa, na mesma
     * chave, com o mesmo conteúdo em tudo o resto.
     *
     * **Uma atualização RE-ANCORA**, e é por isso que ela toca na base: a
     * derivação do V28I.Z.3 recomeça no que a sala acabou de dizer em vez de
     * continuar a partir de uma âncora que ela contradisse. E a apresentação
     * re-ancora no mesmo passo — deixá-la na posição extrapolada antiga seria um
     * salto entre o que a sala corrigiu e o que o ecrã mostrava.
     *
     * **Um id desconhecido é IGNORADO**, como em todas as outras famílias.
     *
     * Uma atualização diz onde um corpo está e para onde vai. Ela **não** diz o
     * que ele é: o dono, o tipo, a carta, o raio da explosão e a marca de
     * linearidade viajam na ENTRADA e em mais lado nenhum. Construir um corpo a
     * partir dela seria inventar essas cinco coisas — e o que saía do outro lado
     * era um tiro comum desenhado por cima de uma Esfera Ígnea.
     *
     * É a política que os planetas, os cometas, os alienígenas e os bosses já
     * seguem, à letra, três linhas acima de cada um deles. O corpo volta a ser
     * conhecido quando a sala mandar a entrada completa, e uma ressincronização
     * manda-as todas.
     */
    for (const [id, x, y, vx, vy] of frame.pm ?? []) {
      const shot = shots.get(id);
      if (shot === undefined) {
        continue;
      }
      const base = shot.base;
      base.vx = vx;
      base.vy = vy;
      base.baseX = x;
      base.baseY = y;
      base.baseTick = frame.k;
      shot.x = x;
      shot.y = y;
    }
    /*
     * A saída de escape, para uma atualização que não tenha os cinco valores. A
     * sala não produz nenhuma hoje, e ausente quer dizer "nenhuma".
     */
    for (const moved of frame.pu ?? []) {
      const shot = shots.get(moved.i);
      if (shot === undefined) {
        /* Ignorado, pela mesma razão do lote acima. */
        continue;
      }
      const base = shot.base;
      if (moved.vx !== undefined) {
        base.vx = moved.vx;
      }
      if (moved.vy !== undefined) {
        base.vy = moved.vy;
      }
      base.baseX = moved.x;
      base.baseY = moved.y;
      base.baseTick = frame.k;
      shot.x = moved.x;
      shot.y = moved.y;
    }
    /*
     * **E os corpos lineares avançam pelo tique AUTORITATIVO** (V28I.Z.3).
     *
     * `base + v x (tique - tique_da_base)`, que é literalmente a aritmética da
     * sala: um tique move um corpo exatamente `(vx, vy)` em milli-unidades
     * inteiras, portanto isto reproduz **bit a bit** o número que viajava numa
     * atualização. Não há suavização, não há previsão e não há relógio local: o
     * relógio é o `k` da trama, e uma ligação parada congela os corpos
     * exatamente como congela tudo o resto.
     *
     * O cliente continua a não decidir nada. Um corpo linear só desaparece
     * quando a sala publicar o `px`, e o impacto acontece no ponto do evento
     * autoritativo — nunca nesta posição.
     */
    for (const shot of shots.values()) {
      advanceOpenWorldShot(shot, frame.k);
    }

    this.patch({
      tick: frame.k,
      receivedAt,
      ownSyncAt,
      entities,
      summons: [...summons.values()],
      /*
       * **Substituídos por inteiro, e só quando vêm.**
       *
       * A sala publica-os quando mudam; uma trama silenciosa quer dizer que
       * nada mudou, e apagar a lista nesse caso faria a PT piscar no HUD.
       */
      ...(frame.al === undefined ? {} : { allies: frame.al }),
      /* Efeitos, e não estado: substituídos por inteiro, drenados pela cena. */
      cardBursts: frame.kd ?? [],
      /*
       * Do canal dos efeitos de joia, e SÓ a área: as outras seis coisas que
       * ele carrega — o corte, o roubo, o míssil, a reflexão, a cura vital e a
       * detonação — não têm consumidor neste mundo, e inventar-lhes um aqui
       * seria decidir a apresentação delas de lado.
       */
      blasts: frame.j.flatMap((event) =>
        event.e === 'atomic_blast'
          ? [
              {
                /*
                 * O tique e o autor, compostos: os dois números autoritativos que
                 * nomeiam esta explosão. Nada aqui sorteia.
                 */
                id: event.k * 65_536 + event.p,
                x: event.x,
                y: event.y,
                r: event.r,
              },
            ]
          : [],
      ),
      /*
       * **O array é refeito, e não realocado** (gate V28I.AK): a cena precisa
       * de uma lista, e uma lista nova por trama era mais uma alocação do
       * tamanho da população. Os elementos são os registos persistentes, que a
       * cena lê enquanto voam — ela guarda a trama e desenha a sessenta quadros
       * por segundo a partir de vinte, e é isso que faz um registo vivo ser a
       * resposta certa em vez de uma fotografia de há dois quadros.
       */
      shots: this.refillShots(),
      comets: [...comets.values()],
      cometEvents: frame.cv ?? [],
      planets: [...planets.values()],
      planetEvents: frame.pv2 ?? [],
      mobs: [...mobs.values()],
      mobEvents: frame.mv ?? [],
      bosses: [...bosses.values()],
      bossEvents: frame.bv ?? [],
      bossHoming: [...bossHoming.values()],
      bossCores: [...bossCores.values()],
      embers: [...embers.values()],
      bossCapacity: frame.bc?.[0] ?? null,
      waters: [...waters.values()],
      fishers: frame.fp ?? [],
      /*
       * `undefined` quer dizer "não mudou" e `null` quer dizer "acabou". Sem a
       * distinção, ou o painel fechava a cada trama silenciosa, ou nunca fechava.
       */
      ...(frame.fm === undefined ? {} : { fishing: frame.fm }),
      ...(frame.fw === undefined ? {} : { fishingWater: frame.fw }),
      ...(frame.mk === undefined ? {} : { merchant: frame.mk }),
      ...(frame.mn === undefined ? {} : { merchantNear: frame.mn }),
      ...(frame.kn === undefined ? {} : { nearestBox: frame.kn }),
      ...(frame.ke === undefined && frame.kx === undefined
        ? {}
        : { boxes: this.mergeBoxes(frame.ke ?? [], frame.kx ?? []) }),
    });
  }

  /**
   * **A resposta a uma travessia**, e a segunda fase do handoff.
   *
   * `destination_ready` é o instante em que o destino existe e ainda ninguém se
   * comprometeu. É aqui que a ligação do destino é aberta em segundo plano — a
   * origem continua a desenhar, viva e vulnerável — e só o facto de ela abrir é
   * que deixa a travessia seguir.
   */
  /**
   * **A proposta de travessia coletiva, tal como a sala a conta** (V28I.T).
   *
   * Guardada inteira e nunca interpretada: quem é elegível, quem já
   * confirmou, em que estado está a máquina e quanto tempo resta são todos
   * campos da autoridade. O cliente não acrescenta um integrante, não retira
   * um, e não conclui que a proposta pode partir.
   *
   * ## Quando ela desaparece
   *
   * Quando acaba, e acaba de quatro maneiras. Uma **recusa** traz `r` e é
   * dita por palavras. Uma **retirada** de qualquer integrante chega aqui
   * como a mesma recusa, porque é o que ela é do lado da sala. Uma
   * **expiração** chega como recusa também. E um **compromisso** fecha-a: o
   * casco muda de sala, e a proposta seguinte, se existir, virá da nova.
   *
   * Em nenhum desses casos ela é reposta: o painel não pode ressuscitar uma
   * proposta terminada porque não há mais nada que a escreva sem um `xg` novo,
   * e um `xg` novo traz um `p` diferente.
   */
  private applyCrossing(frame: OpenWorldCrossingResult): void {
    this.patch({ crossing: frame, phase: frame.s === null ? this.state.phase : 'crossing' });
    if (frame.r !== null) {
      /*
       * **Uma travessia recusada tem de se ver.** Isto não dizia nada: o painel
       * ficava aberto, a nave ficava onde estava, e o jogador não tinha como
       * distinguir uma recusa de um clique que não registou.
       */
      this.flash(crossingRefusalText(frame.r));
      return;
    }
    if (frame.s !== 'destination_ready' || frame.q === undefined || this.pending !== null) {
      return;
    }
    void this.openPending(frame.q);
  }

  /**
   * Abre a ligação do destino **sem tocar na que está a desenhar**.
   *
   * Ela fica calada até a troca: os seus frames são guardados no estado só
   * depois de a origem fechar, portanto não há um instante em que duas salas
   * descrevam a mesma nave.
   */
  private async openPending(quadrant: OpenWorldCoordinate): Promise<void> {
    const generation = this.generation;
    const grant = await this.deps.mintTicket(quadrant).catch(() => null);
    if (grant === null || generation !== this.generation) {
      return;
    }
    /*
     * O destino pode estar noutro host: cada quadrante tem a sua autoridade, e
     * é o registo que diz qual. O bilhete continua a não ir na URL.
     */
    const direct = grant.socketUrl !== null;
    const socket = this.deps.openSocket(
      direct ? (grant.socketUrl as string) : this.deps.socketUrl(grant.ticket),
    );
    this.pending = { socket, quadrant, attachTicket: direct ? grant.ticket : null };
  }

  /**
   * **A troca.** A origem fechou; o destino passa a ser a fonte de frames.
   *
   * Tudo o que era visual da origem é largado: entidades, tiros, fronteira. O
   * que atravessa é a nave, e ela chega descrita pelo primeiro frame do destino.
   */
  private swapToPending(): void {
    const pending = this.pending;
    this.pending = null;
    this.socket = null;
    if (pending === null) {
      /*
       * **A origem largou o casco antes de o destino acabar de abrir.**
       *
       * As duas fases chegam em tramas independentes — `xr(destination_ready)`
       * e `c(departed)` — e nada garante a ordem entre elas. Abrir o destino
       * custa uma ida ao servidor para cunhar um bilhete, cerca de cem
       * milissegundos; o compromisso do outro lado é uma conversa entre dois
       * objetos duráveis, e quando ambos estão quentes responde em menos de
       * dez. Medido: 485 ms de folga na primeira travessia de um mundo frio, e
       * **9 ms** na seguinte — a ordem inverte-se, e invertia-se em silêncio.
       *
       * Isto dizia "reconecta-se pelo caminho ordinário" e punha `quadrant` a
       * `null` — mas o caminho ordinário reconecta ao quadrante que está neste
       * campo, portanto não havia caminho nenhum: a queda do socket seguinte
       * encontrava-o vazio e a expedição acabava em `failed`, sem uma palavra.
       *
       * O destino não é uma incógnita: a travessia disse-o, com coordenada, e
       * é a autoridade que o disse. Ligar-lhe é o mesmo que uma reconexão faz —
       * bilhete novo, socket novo — e `connect` sobe a geração, o que faz a
       * abertura atrasada desistir antes de criar um socket que ninguém leria.
       */
      const destination = this.state.crossing?.q ?? null;
      if (destination === null) {
        this.quadrant = null;
        this.patch({ phase: 'reconnecting', notice: 'A mudar de quadrante…' });
        return;
      }
      this.forgetShots();
      this.patch({ entities: [], shots: [], crossing: null, border: null, notice: null });
      this.connect({ rowIndex: destination.rowIndex, columnIndex: destination.columnIndex });
      /*
       * Ainda é uma travessia, e não uma ligação a começar do zero. A fase é
       * reposta DEPOIS de `connect`, que a tinha posto em `authorising` — sem
       * isto o HUD desapareceria durante os cem milissegundos da autorização,
       * que é uma piscadela a meio de uma travessia.
       */
      this.patch({ phase: 'crossing' });
      return;
    }
    this.generation += 1;
    this.quadrant = pending.quadrant;
    this.socket = pending.socket;
    this.nextSeq = 1;
    this.forgetAcks();
    /* Nada do quadrante antigo atravessa — nem um corpo, nem a apresentação dele. */
    this.forgetShots();
    this.patch({
      phase: 'live',
      quadrant: pending.quadrant,
      label: openWorldQuadrantLabel(pending.quadrant),
      /* Nada do quadrante antigo atravessa. O que se vê é o que o destino disser. */
      entities: [],
      shots: [],
      comets: [],
      cometEvents: [],
      planets: [],
      planetEvents: [],
      mobs: [],
      mobEvents: [],
      bosses: [],
      bossEvents: [],
      bossCapacity: null,
      waters: [],
      fishers: [],
      fishing: null,
      fishingWater: null,
      fishingResult: null,
      fishingRefusal: null,
      /* Um quadrante novo tem outro balcão, ou nenhum. Nada atravessa. */
      merchant: null,
      merchantNear: false,
      merchantView: null,
      saleReceipt: null,
      saleRefusal: null,
      /* Uma caixa não atravessa com a nave. O destino diz as suas. */
      boxes: [],
      /*
       * As invocações do destino são as do destino. As deste piloto atravessam
       * na carga do casco e voltam a ser publicadas pela sala de lá; as de
       * outras pessoas ficaram no quadrante anterior.
       */
      summons: [],
      /* Os aliados são do quadrante onde eu estou: o destino diz os dele. */
      allies: [],
      cardBursts: [],
      blasts: [],
      /*
       * Os espaços **não** são limpos: eles atravessam com a nave, e a sala do
       * destino reenvia-os no primeiro quadro. Limpá-los faria a barra piscar
       * numa travessia, que é exatamente o que o contrato proíbe.
       */
      nearestBox: null,
      boxView: null,
      lootResult: null,
      cargoItems: null,
      crossing: null,
      /* A fronteira do quadrante antigo não descreve o novo. O destino di-la. */
      border: null,
      notice: null,
    });
    this.attached = pending.attachTicket === null;
    this.bind(pending.socket, this.generation, pending.attachTicket);
  }

  private transmit(frame: unknown): void {
    if (!this.attached) {
      /*
       * **Antes do `accepted`, o socket não é de ninguém.** O host fecha
       * qualquer coisa que não seja o `attach`, portanto um comando enviado
       * aqui não chegaria tarde: custaria a ligação inteira.
       */
      return;
    }
    try {
      this.socket?.send(JSON.stringify(frame));
    } catch {
      /* Um socket a fechar. A queda chega pelo handler, e é lá que é tratada. */
    }
  }

  private teardownSocket(): void {
    this.cancelRetry?.();
    this.cancelRetry = null;
    try {
      this.socket?.close();
    } catch {
      /* Já fechado. */
    }
    this.socket = null;
  }

  private teardownPending(): void {
    try {
      this.pending?.socket.close();
    } catch {
      /* Já fechado. */
    }
    this.pending = null;
  }

  private patch(next: Partial<OpenWorldClientState>): void {
    this.state = { ...this.state, ...next };
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}

/** O socket real do browser, embrulhado na forma que esta classe conhece. */
export function browserOpenWorldSocket(url: string): OpenWorldSocket {
  const socket = new WebSocket(url);
  /*
   * **Nada do que chega antes de alguém escutar se perde.**
   *
   * Esta é a peça de que o handoff em duas fases depende. O socket do destino é
   * aberto **em segundo plano** e só é ligado aos handlers quando a origem
   * fecha — e no meio desses milissegundos a sala do destino já mandou a sua
   * trama de boas-vindas, que é a única que diz qual entidade é a nossa e qual
   * é o lado do quadrante. Um `addEventListener` registado depois não a
   * recebe: um `WebSocket` do browser não guarda nada.
   *
   * Sem isto, uma travessia chegava ao outro lado com `me = 0` — sem câmara a
   * seguir, sem barra de vida e sem brilho de proteção — e ficava assim até uma
   * reconexão. Guardar e reentregar por ordem é o adaptador a cumprir a única
   * coisa que um transporte promete.
   *
   * A ordem é preservada sempre: mesmo depois de haver quem escute, o que chega
   * passa pela fila e é drenado a seguir, portanto uma mensagem nova nunca
   * ultrapassa uma antiga.
   */
  const backlog: string[] = [];
  let sink: ((data: string) => void) | null = null;
  let closed = false;
  let failed = false;
  let onClose: (() => void) | null = null;
  let onError: (() => void) | null = null;

  const drain = (): void => {
    if (sink === null) {
      return;
    }
    while (backlog.length > 0) {
      const next = backlog.shift();
      if (next !== undefined) {
        sink(next);
      }
    }
  };

  socket.addEventListener('message', (event) => {
    if (typeof event.data === 'string') {
      backlog.push(event.data);
      drain();
    }
  });
  socket.addEventListener('close', () => {
    closed = true;
    onClose?.();
  });
  socket.addEventListener('error', () => {
    failed = true;
    onError?.();
  });

  return {
    send: (data) => socket.send(data),
    close: () => socket.close(),
    onOpen: (handler) => {
      /* Já aberto quando alguém pergunta: a resposta é sim, e agora. */
      if (socket.readyState === WebSocket.OPEN) {
        handler();
        return;
      }
      socket.addEventListener('open', () => handler());
    },
    onMessage: (handler) => {
      sink = handler;
      drain();
    },
    onClose: (handler) => {
      onClose = handler;
      /* Morreu enquanto ninguém escutava. Quem chega agora tem de saber. */
      if (closed) {
        handler();
      }
    },
    onError: (handler) => {
      onError = handler;
      if (failed) {
        handler();
      }
    },
  };
}
